import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import VesselTrackingMap from "./VesselTrackingMap";
import mcbLogo from "../images/mcb-logo.png";
import DateTimePicker from "../components/ui/DateTimePicker";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:4040/api";
const SAILINGS_API = `${API_BASE}/sailings`;
const MANIFEST_API = `${API_BASE}/manifest`;
const VESSELS_API = `${API_BASE}/vessels`;
const BOOKINGS_API = `${API_BASE}/bookings`;

// MPI Table 4 — minimum floor area per head (m²) for voyages < 10 days
const MPI_DENSITY_TABLE = [
  [200, 0.770], [210, 0.804], [220, 0.838], [230, 0.872], [240, 0.906],
  [250, 0.940], [260, 0.974], [270, 1.008], [280, 1.042], [290, 1.076],
  [300, 1.110], [310, 1.144], [320, 1.178], [330, 1.212], [340, 1.246],
  [350, 1.280], [360, 1.314], [370, 1.348], [380, 1.382], [390, 1.416],
  [400, 1.450], [410, 1.478], [420, 1.505], [430, 1.533], [440, 1.560],
  [450, 1.588], [460, 1.615], [470, 1.643], [480, 1.670], [490, 1.698],
  [500, 1.725], [510, 1.753], [520, 1.780], [530, 1.808], [540, 1.835],
  [550, 1.863], [560, 1.890], [570, 1.918], [580, 1.945], [590, 1.973],
  [600, 2.000], [650, 2.150], [700, 3.000],
];

function getMpiMinArea(avgWeightKg) {
  const w = Number(avgWeightKg || 0);
  if (w <= 0) return 0.770;
  for (let i = MPI_DENSITY_TABLE.length - 1; i >= 0; i--) {
    if (w >= MPI_DENSITY_TABLE[i][0]) return MPI_DENSITY_TABLE[i][1];
  }
  return 0.770;
}

function getMpiMaxHead(penAreaM2, avgWeightKg) {
  const minArea = getMpiMinArea(avgWeightKg);
  if (!minArea || !penAreaM2) return 0;
  return Math.floor(penAreaM2 / minArea);
}

const DEFAULT_CAPACITY = {
  livestockHeads: 350,
  weightKg: 250000,
  freightCube: 300,
};

export default function SailingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [selectedBookings, setSelectedBookings] = useState(new Set());

  const [sailing, setSailing] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [apiTotals, setApiTotals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingManifest, setSavingManifest] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [showTrackingMap, setShowTrackingMap] = useState(false);
  const [vessel, setVessel] = useState(null);
  const [generatingPlan, setGeneratingPlan] = useState(false);
  const [selectedPen, setSelectedPen] = useState(null);
  const [reassigning, setReassigning] = useState(false);
  const [moveAllocIndex, setMoveAllocIndex] = useState(0);
  const [moveHeadCount, setMoveHeadCount] = useState("");
  const [printMode, setPrintMode] = useState("manifest");
  const [uploading, setUploading] = useState(false); // "manifest" | "loadplan"
  const [sailingAlertDraft, setSailingAlertDraft] = useState({ message: "", severity: "info" });
  const [savingSailingAlert, setSavingSailingAlert] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  const actionsMenuRef = useRef(null);

  useEffect(() => {
    if (!actionsOpen) return undefined;
    const onDown = (e) => {
      if (actionsMenuRef.current && !actionsMenuRef.current.contains(e.target)) setActionsOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [actionsOpen]);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${MANIFEST_API}/${id}`);
      if (!response.ok) throw new Error("Failed to load manifest");
      const data = await response.json();
      const sailingData = data.sailing || null;
      setSailing(sailingData);
      setBookings(Array.isArray(data.bookings) ? data.bookings : []);
      setApiTotals(data.totals || null);

      // Fetch vessel with pen layout
      const vesselId = sailingData?.vessel?._id || sailingData?.vessel;
      if (vesselId) {
        const vRes = await fetch(`${VESSELS_API}/${vesselId}`);
        if (vRes.ok) {
          const vData = await vRes.json();
          setVessel(vData?.data || vData);
        }
      }
    } catch (error) {
      console.error("Failed to load sailing detail:", error);
      setSailing(null);
      setBookings([]);
      setApiTotals(null);
    } finally {
      setLoading(false);
    }
  };

  const sailingRef = sailing?.sailingRef || sailing?.sailingId || sailing?._id;

  const route =
    sailing?.route ||
    sailing?.routeLabel ||
    `${sailing?.origin || sailing?.departurePort || "-"} \u2192 ${
      sailing?.destination || sailing?.arrivalPort || "-"
    }`;

  const manifestedBookings = useMemo(() => {
    return bookings.filter((booking) => {
      const status = String(booking.status || "").toLowerCase();
      return status !== "cancelled";
    });
  }, [bookings]);

  // Alerts are raised once here and applied to every (non-cancelled) booking
  // on this sailing at once — individual bookings can only display the
  // alert, not set their own (see BookingDetail.jsx). This derives the
  // "current" alert by looking at what's actually saved on the bookings
  // themselves, so it stays in sync even if raised from here previously.
  const currentSailingAlert = useMemo(() => {
    const withAlert = manifestedBookings.find((b) => b.customerAlert?.message);
    return withAlert?.customerAlert || null;
  }, [manifestedBookings]);

  useEffect(() => {
    if (currentSailingAlert) {
      setSailingAlertDraft({
        message: currentSailingAlert.message || "",
        severity: currentSailingAlert.severity || "info",
      });
    }
  }, [currentSailingAlert?.message, currentSailingAlert?.severity]);

  const raiseSailingAlert = async () => {
    if (!sailingAlertDraft.message.trim()) {
      alert("Enter an alert message, or use Clear Alert to remove an existing one.");
      return;
    }
    if (manifestedBookings.length === 0) {
      alert("There are no active bookings on this sailing to notify.");
      return;
    }
    const confirmed = window.confirm(
      `Raise this alert on all ${manifestedBookings.length} booking${manifestedBookings.length > 1 ? "s" : ""} on this sailing?`
    );
    if (!confirmed) return;

    try {
      setSavingSailingAlert(true);
      const customerAlert = {
        message: sailingAlertDraft.message.trim(),
        severity: sailingAlertDraft.severity,
        setAt: new Date().toISOString(),
        // NOTE: as with the per-booking version this replaced, there's no
        // logged-in-staff identity available in this component — wire this
        // up to your actual auth/user context if you want the real staff
        // member's name recorded here instead of this placeholder.
        setBy: "Staff",
      };

      const results = await Promise.all(
        manifestedBookings.map((b) =>
          fetch(`${BOOKINGS_API}/${b._id}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ ...b, customerAlert }),
          }).then((res) => ({ ok: res.ok, id: b._id }))
        )
      );

      const failed = results.filter((r) => !r.ok);
      if (failed.length > 0) {
        alert(`${failed.length} of ${manifestedBookings.length} bookings could not be updated.`);
      }
      await loadData();
    } catch (error) {
      console.error(error);
      alert(error.message);
    } finally {
      setSavingSailingAlert(false);
    }
  };

  const clearSailingAlert = async () => {
    if (!window.confirm(`Clear this alert from all ${manifestedBookings.length} booking(s) on this sailing?`)) return;

    try {
      setSavingSailingAlert(true);
      const emptyAlert = { message: "", severity: "info", setAt: null, setBy: "" };

      const results = await Promise.all(
        manifestedBookings.map((b) =>
          fetch(`${BOOKINGS_API}/${b._id}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ ...b, customerAlert: emptyAlert }),
          }).then((res) => ({ ok: res.ok, id: b._id }))
        )
      );

      const failed = results.filter((r) => !r.ok);
      if (failed.length > 0) {
        alert(`${failed.length} of ${manifestedBookings.length} bookings could not be updated.`);
      }
      setSailingAlertDraft({ message: "", severity: "info" });
      await loadData();
    } catch (error) {
      console.error(error);
      alert(error.message);
    } finally {
      setSavingSailingAlert(false);
    }
  };

  const totals = useMemo(() => {
    // Always sum straight from manifestedBookings — the same data source
    // "Manifested Bookings" and "Revenue" already use. The backend's
    // pre-aggregated sailing.totals (apiTotals) has repeatedly gone stale
    // relative to the real booking data (e.g. showing 0 livestock head on
    // a sailing with real livestock/freight bookings), so it's no longer
    // trusted here even when present.
    let livestockHeads = 0;
    let weightKg = 0;
    let freightCube = 0;
    let revenue = 0;

    manifestedBookings.forEach((booking) => {
      // charges.total is the real, calculated figure — set by "Calculate
      // Charges" (BookingDetail.jsx) or a paid Stripe checkout — and must be
      // checked first. pricingSnapshot/totals.estimatedRevenue are earlier,
      // pre-pricing estimates; reading those first (as this used to) shows a
      // stale lower number even after a booking has been properly priced.
      revenue += Number(
        booking?.charges?.total ||
          booking?.pricingSnapshot?.calculatedTotal ||
          booking?.totals?.estimatedRevenue ||
          booking?.invoiceSnapshot?.total ||
          0
      );

      (booking.livestockItems || []).forEach((item) => {
        const heads = Number(item.headCount || 0);
        const averageWeight = Number(item.averageWeightKg || 0);
        livestockHeads += heads;
        weightKg += heads * averageWeight;
      });
      (booking.freightItems || []).forEach((item) => {
        weightKg += Number(item.weightKg || 0);
        freightCube += Number(item.cubeM3 || 0);
      });
    });

    return { livestockHeads, weightKg, freightCube, revenue };
  }, [manifestedBookings]);

  const livestockCapacity =
    Number(sailing?.capacity?.maxLivestockHead) || DEFAULT_CAPACITY.livestockHeads;
  const weightCapacity =
    Number(sailing?.capacity?.maxWeightKg) || DEFAULT_CAPACITY.weightKg;
  const freightCapacity =
    Number(sailing?.capacity?.maxCubeM3) || DEFAULT_CAPACITY.freightCube;

  const livestockPercent = getPercent(totals.livestockHeads, livestockCapacity);
  const weightPercent = getPercent(totals.weightKg, weightCapacity);
  const freightPercent = getPercent(totals.freightCube, freightCapacity);

  const savedManifest = sailing?.manifestSnapshot;
  const manifestLocked = Boolean(sailing?.manifestLocked);

  // Click-to-edit save for a single date field on the sailing (ETD / ETA / booking cut-off).
  // Uses the same full-document PUT as the rest of this page so it works with the existing API.
  const saveSailingDateField = async (field, localValue) => {
    if (!sailing?._id) return;
    const iso = localValue ? new Date(localValue).toISOString() : null;
    const response = await fetch(`${SAILINGS_API}/${sailing._id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...sailing, [field]: iso }),
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || "Failed to save");
    }
    setSailing((prev) => ({ ...prev, [field]: iso }));
  };

  const generateManifest = async () => {
    if (!sailing?._id) return;
    try {
      setSavingManifest(true);
      const manifestSnapshot = {
        manifestNumber:
          sailing?.manifestSnapshot?.manifestNumber || `MAN-${Date.now()}`,
        status: "generated",
        generatedAt: new Date().toISOString(),
        sailingId: sailing?._id || "",
        sailingRef,
        route,
        vesselName: sailing?.vessel?.name || sailing?.vesselName || "",
        etd: sailing?.etd || "",
        eta: sailing?.eta || "",
        totals,
        bookingRefs: manifestedBookings.map(
          (booking) =>
            booking.bookingRef ||
            booking.ref ||
            booking.reference ||
            booking._id?.slice(-6).toUpperCase()
        ),
      };
      const response = await fetch(`${SAILINGS_API}/${sailing._id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...sailing, manifestSnapshot }),
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Failed to generate manifest");
      }
      await loadData();
    } catch (error) {
      console.error(error);
      alert(error.message);
    } finally {
      setSavingManifest(false);
    }
  };

  const updateBookingManifestStatus = async (
    bookingId,
    manifestStatus,
    shortShippedReason = ""
  ) => {
    if (!bookingId) return;
    try {
      setSavingManifest(true);
      const body = { manifestStatus };
      if (manifestStatus === "Short Shipped") {
        body.shortShippedReason =
          shortShippedReason ||
          window.prompt("Reason for short shipping?", "") ||
          "";
      }
      const response = await fetch(
        `${MANIFEST_API}/booking/${bookingId}/status`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }
      );
      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || "Failed to update manifest status");
      }
      await loadData();
    } catch (error) {
      console.error(error);
      alert(error.message);
    } finally {
      setSavingManifest(false);
    }
  };

  const runSailingAction = async (action) => {
    if (!sailing?._id) return;
    try {
      setSavingManifest(true);
      const response = await fetch(`${MANIFEST_API}/${sailing._id}/${action}`, {
        method: "PATCH",
      });
      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || `Failed to ${action} sailing`);
      }
      await loadData();
    } catch (error) {
      console.error(error);
      alert(error.message);
    } finally {
      setSavingManifest(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="rounded-2xl bg-white border border-slate-200 p-8 shadow-sm text-sm text-slate-500">
          Loading sailing...
        </div>
      </div>
    );
  }

  if (!sailing) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="rounded-2xl bg-white border border-slate-200 p-8 shadow-sm text-sm text-slate-500">
          Sailing not found.
        </div>
      </div>
    );
  }

  const status = String(sailing.status || "").toLowerCase();
  const isLoading = status === "loading";
  const isDeparted = status === "departed";
  const isArrived = status === "arrived";
  const isClosed = status === "closed";

  const WORKFLOW_STEPS = ["draft", "open", "loading", "departed", "arrived", "closed"];
  const currentStepIndex = Math.min(
    WORKFLOW_STEPS.indexOf(status) >= 0 ? WORKFLOW_STEPS.indexOf(status) : 0,
    WORKFLOW_STEPS.length - 1
  );

  const BASE_TABS = ["overview", "bookings", "manifest"];
  const TABS = [...BASE_TABS, "loading", "documents"];

  return (
    <>
      <div className="no-print">
        <div className="w-full p-6">

          {/* ── Page header ── */}
          <div className="mb-5">
            {/* Top row — back + status + actions */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
              <div className="flex items-center gap-2">
                <Link
                  to="/dashboard/sailings"
                  className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-900"
                >
                  ← Sailings
                </Link>
                <span className="text-slate-300">/</span>
                <StatusBadge status={sailing.status} />
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {/* Primary action — the single next step for this status */}
                {status === "draft" && (
                  <button type="button" onClick={() => runSailingAction("open")} disabled={savingManifest}
                    className="rounded-xl bg-slate-950 px-5 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50">
                    Open Sailing
                  </button>
                )}
                {status === "open" && (
                  <button type="button" onClick={() => runSailingAction("loading")} disabled={savingManifest}
                    className="rounded-xl bg-slate-950 px-5 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50">
                    Start Loading
                  </button>
                )}
                {status === "loading" && (
                  <button type="button" onClick={() => runSailingAction("depart")} disabled={savingManifest}
                    className="rounded-xl bg-slate-950 px-5 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50">
                    Depart
                  </button>
                )}
                {status === "departed" && (
                  <button type="button" onClick={() => runSailingAction("arrive")} disabled={savingManifest}
                    className="rounded-xl bg-slate-950 px-5 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50">
                    Arrive
                  </button>
                )}
                {status === "arrived" && (
                  <button type="button" onClick={() => runSailingAction("close")} disabled={savingManifest}
                    className="rounded-xl bg-slate-950 px-5 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50">
                    Close Sailing
                  </button>
                )}

                <Link to={`/dashboard/sailings/${sailing._id}/edit`}
                  className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                  Edit
                </Link>

                {/* Everything else lives in one menu */}
                <div className="relative" ref={actionsMenuRef}>
                  <button
                    type="button"
                    onClick={() => setActionsOpen((o) => !o)}
                    className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    Actions ▾
                  </button>

                  {actionsOpen && (
                    <div className="absolute right-0 z-20 mt-2 w-72 rounded-2xl border border-slate-200 bg-white p-2 shadow-xl">
                      {status !== "closed" && (
                        <>
                          <div className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                            Bookings
                          </div>
                          <Link
                            to={`/dashboard/bookings/new?sailingId=${sailing._id}`}
                            onClick={() => setActionsOpen(false)}
                            className="block rounded-lg px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                          >
                            + Add Booking
                          </Link>
                        </>
                      )}

                      <div className={`px-3 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wide text-slate-400 ${status !== "closed" ? "mt-1 border-t border-slate-100" : ""}`}>
                        Manifest
                      </div>
                      {status === "open" && (
                        <button
                          type="button"
                          disabled={savingManifest || manifestLocked}
                          onClick={() => { setActionsOpen(false); runSailingAction("lock"); }}
                          className="block w-full rounded-lg px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-40"
                        >
                          {manifestLocked ? "Manifest Locked" : "Lock Manifest"}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => { setActionsOpen(false); window.open(`/dashboard/sailings/${sailing._id}/manifest/print`, "_blank"); }}
                        className="block w-full rounded-lg px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
                      >
                        Print Manifest
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Title row */}
            <div className="flex items-baseline gap-3">
              <h1 className="text-2xl font-bold tracking-tight text-slate-950">{sailingRef || "Sailing"}</h1>
              <span className="text-slate-400 text-sm">{route}</span>
            </div>
          </div>

          {/* ── Workflow stepper ── */}
          <div className="mb-6 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="flex">
              {WORKFLOW_STEPS.map((step, index) => {
                const isDone = currentStepIndex > index;
                const isActive = currentStepIndex === index;
                return (
                  <div
                    key={step}
                    className={`flex flex-1 flex-col items-center justify-center py-3 text-center text-xs font-semibold border-r border-slate-100 last:border-r-0 transition-colors ${
                      isActive
                        ? "bg-slate-950 text-white"
                        : isDone
                        ? "bg-slate-100 text-slate-500"
                        : "bg-white text-slate-400"
                    }`}
                  >
                    <span className={`mb-0.5 text-[10px] font-normal uppercase tracking-wider ${isActive ? "opacity-70" : "opacity-50"}`}>
                      {index + 1}
                    </span>
                    {toTitle(step)}
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Tabs ── */}
          <div className="mb-6 flex flex-wrap gap-1.5 border-b border-slate-200 pb-4">
            {TABS.map((tab) => {
              const label = tab === "loading" ? "Loading" : toTitle(tab);
              const isConditional = false;
              return (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${
                    activeTab === tab
                      ? "bg-slate-950 text-white shadow-sm"
                      : isConditional
                      ? "bg-emerald-50 border border-emerald-200 text-emerald-700 hover:bg-emerald-100"
                      : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>

          {/* ── Main content + Sidebar ── */}
          <div className="grid gap-6 xl:grid-cols-[1.65fr_0.85fr]">

            {/* Main content */}
            <div className="space-y-6">
              <div className="space-y-6">

              {/* OVERVIEW TAB */}
              {activeTab === "overview" && (
                <>
                  <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                    <MetricCard label="Manifested Bookings" value={manifestedBookings.length} />
                    <MetricCard label="Revenue" value={`$${totals.revenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} />
                    <MetricCard label="Livestock" value={`${totals.livestockHeads} / ${livestockCapacity}`} />
                    <MetricCard label="Weight" value={`${totals.weightKg.toLocaleString()} kg`} />
                  </div>

                  <SectionCard title="Capacity Utilisation">
                    <div className="space-y-5">
                      <CapacityBar
                        label="Livestock"
                        value={totals.livestockHeads}
                        total={livestockCapacity}
                        percent={livestockPercent}
                      />
                      <CapacityBar
                        label="Weight"
                        value={totals.weightKg}
                        total={weightCapacity}
                        percent={weightPercent}
                        suffix="kg"
                      />
                      <CapacityBar
                        label="m³"
                        value={totals.freightCube}
                        total={freightCapacity}
                        percent={freightPercent}
                        suffix="m³"
                      />
                    </div>
                  </SectionCard>

                  <SectionCard title="Operational Summary">
                    <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                      <SummaryTile
                        label="Remaining Livestock"
                        value={Math.max(livestockCapacity - totals.livestockHeads, 0).toLocaleString()}
                      />
                      <SummaryTile
                        label="Remaining Weight"
                        value={`${Math.max(weightCapacity - totals.weightKg, 0).toLocaleString()} kg`}
                      />
                      <SummaryTile
                        label="Remaining Cube"
                        value={`${Math.max(freightCapacity - totals.freightCube, 0).toLocaleString()} m³`}
                      />
                      <SummaryTile
                        label="Revenue / Booking"
                        value={`$${Math.round(
                          totals.revenue / Math.max(manifestedBookings.length, 1)
                        ).toLocaleString()}`}
                      />
                    </div>
                  </SectionCard>

                  <SectionCard
                    title="Alerts"
                    description="Raising an alert here notifies every booking on this sailing at once — bookings can't raise their own individually."
                  >
                    {currentSailingAlert?.message && (
                      <div className={`mb-4 rounded-xl border px-4 py-3 text-sm ${
                        currentSailingAlert.severity === "critical" ? "border-red-200 bg-red-50 text-red-800" :
                        currentSailingAlert.severity === "warning" ? "border-amber-200 bg-amber-50 text-amber-800" :
                        "border-blue-200 bg-blue-50 text-blue-800"
                      }`}>
                        <div className="font-semibold">Currently active on {manifestedBookings.filter(b => b.customerAlert?.message).length} of {manifestedBookings.length} bookings:</div>
                        <div className="mt-0.5">{currentSailingAlert.message}</div>
                        {currentSailingAlert.setAt && (
                          <div className="mt-1.5 text-xs opacity-70">
                            Set {new Date(currentSailingAlert.setAt).toLocaleString("en-NZ", { dateStyle: "medium", timeStyle: "short" })}
                            {currentSailingAlert.setBy ? ` by ${currentSailingAlert.setBy}` : ""}
                          </div>
                        )}
                      </div>
                    )}

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-[1fr_auto]">
                      <div>
                        <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">Message</label>
                        <input
                          type="text"
                          value={sailingAlertDraft.message}
                          onChange={(e) => setSailingAlertDraft((prev) => ({ ...prev, message: e.target.value }))}
                          placeholder="e.g. Sailing delayed 2 days due to weather"
                          className="w-full rounded-xl border border-slate-300 px-3.5 py-2.5 text-sm outline-none focus:border-slate-500"
                        />
                      </div>
                      <div>
                        <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">Severity</label>
                        <select
                          value={sailingAlertDraft.severity}
                          onChange={(e) => setSailingAlertDraft((prev) => ({ ...prev, severity: e.target.value }))}
                          className="rounded-xl border border-slate-300 px-3.5 py-2.5 text-sm outline-none focus:border-slate-500"
                        >
                          <option value="info">Info</option>
                          <option value="warning">Warning</option>
                          <option value="critical">Critical</option>
                        </select>
                      </div>
                    </div>

                    <div className="mt-4 flex gap-2">
                      <button
                        type="button"
                        onClick={raiseSailingAlert}
                        disabled={savingSailingAlert}
                        className="rounded-xl bg-slate-950 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
                      >
                        {savingSailingAlert ? "Saving..." : currentSailingAlert?.message ? "Update Alert on All Bookings" : "Raise Alert on All Bookings"}
                      </button>
                      {currentSailingAlert?.message && (
                        <button
                          type="button"
                          onClick={clearSailingAlert}
                          disabled={savingSailingAlert}
                          className="rounded-xl border border-red-200 bg-white px-4 py-2 text-sm font-semibold text-red-600 hover:bg-red-50 disabled:opacity-50"
                        >
                          Clear from All Bookings
                        </button>
                      )}
                    </div>
                  </SectionCard>

                  {vessel?.mmsi && (
                    <SectionCard
                      title="Live Position"
                      action={
                        <button
                          onClick={() => setShowTrackingMap((v) => !v)}
                          className="text-xs font-semibold text-blue-600 hover:underline"
                        >
                          {showTrackingMap ? "Hide map" : "Track vessel"}
                        </button>
                      }
                    >
                      {showTrackingMap ? (
                        <VesselTrackingMap mmsi={vessel.mmsi} vesselName={vessel.name} height={400} />
                      ) : (
                        <p className="text-sm text-slate-400">
                          Click "Track vessel" above to show {vessel.name}'s live AIS position via MarineTraffic.
                        </p>
                      )}
                    </SectionCard>
                  )}
                </>
              )}

              {/* MANIFEST TAB */}
              {activeTab === "manifest" && (
                <SectionCard title="Voyage Manifest">
                  <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <h3 className="text-lg font-bold text-slate-950">
                        {savedManifest?.manifestNumber || "Live Manifest"}
                      </h3>
                      <p className="mt-1 text-sm text-slate-500">
                        Live loading workflow from bookings assigned to this sailing.
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => runSailingAction("lock")}
                        disabled={savingManifest || manifestLocked}
                        className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40"
                      >
                        Lock Manifest
                      </button>
                      <button
                        type="button"
                        onClick={generateManifest}
                        disabled={savingManifest}
                        className="rounded-xl bg-slate-950 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-40"
                      >
                        {savingManifest ? "Saving..." : "Snapshot Manifest"}
                      </button>
                    </div>
                  </div>

                  <div className="mb-6 grid grid-cols-2 gap-4 md:grid-cols-4">
                    <MetricCard label="Bookings" value={manifestedBookings.length} />
                    <MetricCard label="Livestock" value={totals.livestockHeads} />
                    <MetricCard label="Weight" value={`${Number(totals.weightKg || 0).toLocaleString()} kg`} />
                    <MetricCard label="Revenue" value={`$${Number(totals.revenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} />
                  </div>

                  <ManifestWorkflowTable
                    bookings={manifestedBookings}
                    saving={savingManifest}
                    onStatusChange={updateBookingManifestStatus}
                  />

                  <div className="mt-8">
                    <ManifestTables manifestedBookings={manifestedBookings} />
                  </div>
                </SectionCard>
              )}

              {/* BOOKINGS TAB */}
              {activeTab === "bookings" && (
                <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
                  {/* Header */}
                  <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
                    <div>
                      <h2 className="text-base font-semibold text-gray-900">Bookings</h2>
                      <p className="text-sm text-gray-500">
                        Showing {bookings.length} booking{bookings.length !== 1 ? "s" : ""}.
                        {selectedBookings.size > 0 && <span className="ml-2 font-semibold text-gray-800">{selectedBookings.size} selected.</span>}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {selectedBookings.size > 0 && (
                        <>
                          <button
                            className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                            onClick={async () => {
                              const nextRef = window.prompt("Roll selected bookings to sailing ref:");
                              if (!nextRef) return;
                              const token = localStorage.getItem("token");
                              const sRes = await fetch(`${API_BASE}/sailings?search=${nextRef}`);
                              const sData = await sRes.json();
                              const next = Array.isArray(sData) ? sData[0] : sData?.data?.[0];
                              if (!next) { alert("Sailing not found"); return; }
                              for (const bid of selectedBookings) {
                                const b = bookings.find(x => x._id === bid);
                                if (!b) continue;
                                await fetch(`${API_BASE}/bookings/${bid}`, {
                                  method: "PUT", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                                  body: JSON.stringify({ ...b, sailing: next._id, requestedSailingRef: next.sailingRef }),
                                }).catch(console.error);
                              }
                              setSelectedBookings(new Set());
                              await loadData();
                            }}
                          >
                            Roll selected
                          </button>
                          <button
                            className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50"
                            onClick={async () => {
                              if (!window.confirm(`Remove ${selectedBookings.size} booking(s) from this sailing?`)) return;
                              const token = localStorage.getItem("token");
                              for (const bid of selectedBookings) {
                                const b = bookings.find(x => x._id === bid);
                                if (!b) continue;
                                await fetch(`${API_BASE}/bookings/${bid}`, {
                                  method: "PUT", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                                  body: JSON.stringify({ ...b, sailing: null, requestedSailingRef: "" }),
                                }).catch(console.error);
                              }
                              setSelectedBookings(new Set());
                              await loadData();
                            }}
                          >
                            Remove selected
                          </button>
                        </>
                      )}
                      <Link to={`/dashboard/bookings/new?sailingId=${sailing._id}`}
                        className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50">
                        + Add Booking
                      </Link>
                    </div>
                  </div>

                  {/* Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-4 py-3 w-10">
                            <input type="checkbox"
                              checked={bookings.length > 0 && selectedBookings.size === bookings.length}
                              onChange={() => setSelectedBookings(
                                selectedBookings.size === bookings.length ? new Set() : new Set(bookings.map(b => b._id))
                              )}
                              className="h-4 w-4 rounded border-gray-300"
                            />
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Ref</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Customer</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Type</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Status</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Invoice</th>
                          <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">View</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bookings.length === 0 ? (
                          <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-400">No bookings yet.</td></tr>
                        ) : bookings.map((booking, i) => {
                          const ref = booking.bookingRef || booking._id?.slice(-6).toUpperCase();
                          const isSelected = selectedBookings.has(booking._id);
                          const isConfirmed = booking.status === "confirmed";
                          return (
                            <tr key={booking._id}
                              className={`border-t border-gray-200 hover:bg-gray-50 transition-colors ${isSelected ? "bg-blue-50" : ""}`}
                            >
                              <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                                <input type="checkbox" checked={isSelected}
                                  onChange={() => setSelectedBookings(prev => {
                                    const next = new Set(prev);
                                    next.has(booking._id) ? next.delete(booking._id) : next.add(booking._id);
                                    return next;
                                  })}
                                  className="h-4 w-4 rounded border-gray-300"
                                />
                              </td>
                              <td className="px-4 py-3 font-medium text-gray-900 cursor-pointer" onClick={() => navigate(`/dashboard/bookings/${booking._id}`)}>{ref}</td>
                              <td className="px-4 py-3 text-gray-700 cursor-pointer" onClick={() => navigate(`/dashboard/bookings/${booking._id}`)}>{booking.customerName || "—"}</td>
                              <td className="px-4 py-3 text-gray-500 capitalize cursor-pointer" onClick={() => navigate(`/dashboard/bookings/${booking._id}`)}>{booking.bookingType || "freight"}</td>
                              <td className="px-4 py-3 cursor-pointer" onClick={() => navigate(`/dashboard/bookings/${booking._id}`)}>
                                <StatusBadge status={booking.status} />
                              </td>
                              <td className="px-4 py-3 text-gray-400 text-xs cursor-pointer" onClick={() => navigate(`/dashboard/bookings/${booking._id}`)}>{booking.invoice?.invoiceStatus || "—"}</td>
                              <td className="px-4 py-3 text-right" onClick={e => e.stopPropagation()}>
                                <div className="flex items-center justify-end gap-1.5">
                                  <Link to={`/dashboard/bookings/${booking._id}`}
                                    className="rounded border border-gray-300 px-3 py-1 text-xs font-medium hover:bg-gray-100">
                                    Open
                                  </Link>
                                  {!isConfirmed && (
                                    <button className="rounded border border-gray-300 px-3 py-1 text-xs font-medium hover:bg-gray-100"
                                      onClick={async () => {
                                        const token = localStorage.getItem("token");
                                        await fetch(`${API_BASE}/bookings/${booking._id}`, {
                                          method: "PUT", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                                          body: JSON.stringify({ ...booking, status: "confirmed" }),
                                        }).catch(console.error);
                                        await loadData();
                                      }}>Confirm</button>
                                  )}
                                  <button className="rounded border border-gray-300 px-3 py-1 text-xs font-medium hover:bg-gray-100"
                                    onClick={async () => {
                                      const nextRef = window.prompt("Roll to sailing ref:");
                                      if (!nextRef) return;
                                      const token = localStorage.getItem("token");
                                      const sRes = await fetch(`${API_BASE}/sailings?search=${nextRef}`);
                                      const sData = await sRes.json();
                                      const next = Array.isArray(sData) ? sData[0] : sData?.data?.[0];
                                      if (!next) { alert("Sailing not found"); return; }
                                      await fetch(`${API_BASE}/bookings/${booking._id}`, {
                                        method: "PUT", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                                        body: JSON.stringify({ ...booking, sailing: next._id, requestedSailingRef: next.sailingRef }),
                                      }).catch(console.error);
                                      await loadData();
                                    }}>Roll</button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* PEN MODAL */}
              {selectedPen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
                  <div className="w-full max-w-lg rounded-3xl bg-white shadow-xl overflow-hidden">
                    {/* Header */}
                    <div className={`px-6 py-5 ${selectedPen.isHospitalPen ? "bg-red-50 border-b border-red-100" : "bg-slate-50 border-b border-slate-100"}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-lg font-bold text-slate-950">
                            {selectedPen.penLabel || selectedPen.penNumber}
                            {selectedPen.isHospitalPen && <span className="ml-2 text-xs font-bold text-red-600 bg-red-100 px-2 py-0.5 rounded-full">Hospital</span>}
                          </h3>
                          <p className="text-sm text-slate-500 mt-0.5">
                            {selectedPen.areaM2 ? `${selectedPen.areaM2} m²` : ""}
                            {selectedPen.areaM2 && selectedPen.allocations?.[0]?.avgWeightKg
                              ? ` · Max ${Math.floor(selectedPen.areaM2 / getMpiMinArea(Number(selectedPen.allocations[0].avgWeightKg)))} head at ${selectedPen.allocations[0].avgWeightKg}kg`
                              : ` · Max ${selectedPen.maxHead} head`}
                            {sailing.loadPlan?.mpiMinAreaPerHead && ` · MPI: ${sailing.loadPlan.mpiMinAreaPerHead} m²/head`}
                          </p>
                        </div>
                        <button onClick={() => setSelectedPen(null)} className="text-slate-400 hover:text-slate-700 text-2xl leading-none">×</button>
                      </div>
                    </div>

                    <div className="px-6 py-5 space-y-4">
                      {/* Current allocations */}
                      {selectedPen.isHospitalPen ? (
                        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                          Hospital pen — reserved for sick or injured animals. Do not pre-allocate.
                        </div>
                      ) : selectedPen.allocations?.length > 0 ? (
                        <div>
                          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">Current Allocation</div>
                          {selectedPen.allocations.map((alloc, i) => {
                            const booking = bookings.find(b => String(b._id) === String(alloc.bookingId));
                            const livestock = booking?.livestockItems?.find(li => (li.animalClass || li.livestockType) === alloc.animalClass) || booking?.livestockItems?.[0];
                            const alerts = [];
                            if (["bull","entire"].includes(alloc.animalClass?.toLowerCase())) alerts.push({ label: "Bull / Entire", color: "bg-red-100 text-red-700" });
                            if (livestock?.specialStatus?.toLowerCase().includes("horn") || booking?.notes?.toLowerCase().includes("horn")) alerts.push({ label: "Horned", color: "bg-orange-100 text-orange-700" });
                            if (livestock?.specialStatus) alerts.push({ label: livestock.specialStatus, color: "bg-amber-100 text-amber-700" });
                            return (
                              <div key={i} className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 mb-2">
                                <div className="flex items-center justify-between mb-2">
                                  <div className="font-semibold text-sm text-slate-900">{alloc.customerName}</div>
                                  <div className="text-sm font-bold text-slate-900">{alloc.headCount} head</div>
                                </div>
                                <div className="grid grid-cols-3 gap-2 mb-2">
                                  <div>
                                    <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-0.5">Species</div>
                                    <div className="text-xs font-semibold text-slate-700 capitalize">{livestock?.livestockType || "—"}</div>
                                  </div>
                                  <div>
                                    <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-0.5">Class</div>
                                    <div className="text-xs font-semibold text-slate-700 capitalize">{alloc.animalClass || "—"}</div>
                                  </div>
                                  <div>
                                    <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-0.5">Avg Weight</div>
                                    <div className="text-xs font-semibold text-slate-700">{alloc.avgWeightKg ? `${alloc.avgWeightKg} kg` : "—"}</div>
                                  </div>
                                </div>
                                {alerts.length > 0 && (
                                  <div className="flex flex-wrap gap-1.5">
                                    {alerts.map((alert, ai) => (
                                      <span key={ai} className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${alert.color}`}>⚠ {alert.label}</span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-400 text-center">
                          No animals allocated to this pen.
                        </div>
                      )}

                      {/* Reassign to another pen */}
                      {!selectedPen.isHospitalPen && sailing.loadPlan?.status !== "approved" && selectedPen.allocations?.length > 0 && (
                        <div>
                          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">Move to Another Pen</div>

                          {selectedPen.allocations.length > 1 && (
                            <div className="mb-3">
                              <label className="block text-xs text-slate-500 mb-1">Which allocation to move</label>
                              <select
                                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-slate-950"
                                value={moveAllocIndex}
                                onChange={e => { setMoveAllocIndex(Number(e.target.value)); setMoveHeadCount(""); }}
                              >
                                {selectedPen.allocations.map((a, i) => (
                                  <option key={i} value={i}>{a.customerName} — {a.headCount} head ({a.animalClass || "—"})</option>
                                ))}
                              </select>
                            </div>
                          )}

                          {(() => {
                            const sourceAlloc = selectedPen.allocations[moveAllocIndex];
                            const maxMovable = sourceAlloc?.headCount || 0;
                            return (
                              <div className="mb-3">
                                <label className="block text-xs text-slate-500 mb-1">Head Count to Move</label>
                                <input
                                  type="number"
                                  min="1"
                                  max={maxMovable}
                                  value={moveHeadCount}
                                  onChange={e => setMoveHeadCount(e.target.value)}
                                  placeholder={`Max ${maxMovable} (leave blank to move all)`}
                                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-slate-950"
                                />
                              </div>
                            );
                          })()}

                          <div className="grid grid-cols-3 gap-2 max-h-36 overflow-y-auto">
                            {(() => {
                              // Real remaining space depends on the weight
                              // of what's actually being moved — a pen
                              // that's "full" by its static maxHeadCount
                              // may still have genuine MPI floor-area room
                              // for a lighter class, and vice versa.
                              const sourceAlloc = selectedPen.allocations[moveAllocIndex];
                              const movingMpiArea = getMpiMinArea(Number(sourceAlloc?.avgWeightKg || 0));
                              const destPens = (sailing.loadPlan?.pens || vessel?.zones?.map(z => ({ penNumber: z.penNumber, penLabel: z.name, maxHead: z.maxHeadCount, isHospitalPen: z.isHospitalPen, isContainerSlot: z.type === 'freight', maxTEU: z.maxTEU || 0, areaM2: z.areaM2, allocations: [] })) || [])
                                .filter(p => p.penNumber !== selectedPen.penNumber && !p.isHospitalPen && !p.isContainerSlot);
                              return destPens.map((targetPen, i) => {
                                const targetLoad = targetPen.allocations?.reduce((s, a) => s + a.headCount, 0) || 0;
                                const targetArea = Number(targetPen.areaM2 || 0);
                                const targetAreaUsed = (targetPen.allocations || []).reduce(
                                  (s, a) => s + Number(a.headCount || 0) * getMpiMinArea(Number(a.avgWeightKg || 0)),
                                  0
                                );
                                const space = targetArea
                                  ? Math.max(Math.floor((targetArea - targetAreaUsed) / movingMpiArea), 0)
                                  : Math.max((targetPen.maxHead || 0) - targetLoad, 0);
                                return (
                                  <button key={i} type="button" disabled={reassigning || space <= 0}
                                    onClick={async () => {
                                      const sourceAlloc = selectedPen.allocations[moveAllocIndex];
                                      const maxMovable = sourceAlloc?.headCount || 0;
                                      const moveCount = moveHeadCount === "" ? maxMovable : Number(moveHeadCount);

                                      if (!moveCount || moveCount <= 0 || moveCount > maxMovable) {
                                        alert(`Enter a valid head count between 1 and ${maxMovable}.`);
                                        return;
                                      }
                                      if (moveCount > space) {
                                        alert(`Only ${space} head of space in this pen.`);
                                        return;
                                      }

                                      setReassigning(true);
                                      try {
                                        const currentPens = sailing.loadPlan?.pens || [];
                                        const matchesPen = (p, ref) =>
                                          p === ref ||
                                          (p.penNumber && ref.penNumber && String(p.penNumber).trim() === String(ref.penNumber).trim());

                                        const movedAlloc = { ...sourceAlloc, headCount: moveCount };
                                        const isFullMove = moveCount === maxMovable;

                                        const newPens = currentPens.map(p => {
                                          if (matchesPen(p, selectedPen)) {
                                            // Partial move: keep this allocation with the reduced
                                            // headcount instead of clearing the whole pen. Full
                                            // move: drop just this allocation, keep any others.
                                            const remainingAllocations = isFullMove
                                              ? p.allocations.filter((_, i) => i !== moveAllocIndex)
                                              : p.allocations.map((a, i) => i === moveAllocIndex ? { ...a, headCount: a.headCount - moveCount } : a);
                                            return { ...p, allocations: remainingAllocations };
                                          }
                                          if (matchesPen(p, targetPen)) {
                                            return { ...p, allocations: [...(p.allocations || []), movedAlloc] };
                                          }
                                          return p;
                                        });

                                        const totalBefore = currentPens.reduce((s, p) => s + (p.allocations?.reduce((ss, a) => ss + a.headCount, 0) || 0), 0);
                                        const totalAfter = newPens.reduce((s, p) => s + (p.allocations?.reduce((ss, a) => ss + a.headCount, 0) || 0), 0);
                                        if (totalBefore !== totalAfter) {
                                          alert(`Move blocked — total head count would change from ${totalBefore} to ${totalAfter}. This usually means the source pen wasn't matched correctly. Please refresh and try again, or contact support if this persists.`);
                                          setReassigning(false);
                                          return;
                                        }
                                        await fetch(`${SAILINGS_API}/${sailing._id}/loadplan`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ pens: newPens }) });
                                        await loadData(); setSelectedPen(null); setMoveHeadCount("");
                                      } catch (err) { alert(err.message); }
                                      finally { setReassigning(false); }
                                    }}
                                    className={`rounded-lg border px-2.5 py-2 text-left text-xs transition ${space <= 0 ? "border-slate-100 bg-slate-50 opacity-40 cursor-not-allowed" : "border-slate-200 bg-white hover:border-slate-950"}`}
                                  >
                                    <div className="font-bold text-slate-900">{targetPen.penNumber || targetPen.penLabel}</div>
                                    <div className="text-slate-400">{space > 0 ? `${space} free` : "Full"}</div>
                                  </button>
                                );
                              });
                            })()}
                          </div>
                        </div>
                      )}

                      {/* Manually assign a booking to this pen */}
                      {!selectedPen.isHospitalPen && sailing.loadPlan?.status !== "approved" && (
                        <ManualPenAssign
                          pen={selectedPen}
                          bookings={bookings.filter(b => b.bookingType === "livestock" || b.bookingType === "mixed")}
                          existingPens={sailing.loadPlan?.pens || vessel?.zones?.map(z => ({ penNumber: z.penNumber, penLabel: z.name, maxHead: z.maxHeadCount, isHospitalPen: z.isHospitalPen, isContainerSlot: z.type === 'freight', maxTEU: z.maxTEU || 0, areaM2: z.areaM2, allocations: [] })) || []}
                          sailingId={sailing._id}
                          onSaved={async () => { await loadData(); setSelectedPen(null); }}
                        />
                      )}

                      <div className="flex justify-end pt-2">
                        <button onClick={() => setSelectedPen(null)} className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                          Close
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* LOADING TAB */}
              {activeTab === "loading" && (
                <div className="space-y-5">

                  {/* Status banner for pre-loading statuses */}
                  {(status === "draft" || status === "open") && (
                    <div className="rounded-2xl border border-blue-200 bg-blue-50 px-5 py-3 flex items-center gap-3">
                      <div className="text-xs text-blue-700 font-medium">
                        {status === "draft" ? "Sailing is in Draft — " : "Sailing is Open — "}
                        You can build the load plan now as bookings come in. Approve it once loading begins.
                      </div>
                    </div>
                  )}

                  {/* ── PRE-LOADING COMPLIANCE ── */}
                  <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                    <div className="px-5 py-4 border-b border-slate-100">
                      <h2 className="text-base font-semibold text-slate-900">Pre-Loading Compliance</h2>
                      <p className="text-xs text-slate-400 mt-0.5">MNZ Rule Part 24c — tick off each requirement as it's actually completed for this voyage.</p>
                    </div>
                    <div className="p-5 space-y-2">
                      {[
                        { key: "farmerDeclarationReceived", label: "Farmer Declaration Received", ref: "Pre-Loading Step 1" },
                        { key: "hospitalPenReserved", label: "Hospital Pen Reserved", ref: "MNZ Rule Part 24c" },
                        { key: "prestartMeetingCompleted", label: "Prestart Meeting Completed", ref: "Loading Step 1" },
                      ].map(item => {
                        const checked = Boolean(sailing.complianceChecklist?.[item.key]);
                        return (
                          <label key={item.key} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 cursor-pointer hover:bg-slate-100 transition">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={async (e) => {
                                const newChecklist = { ...(sailing.complianceChecklist || {}), [item.key]: e.target.checked };
                                try {
                                  const res = await fetch(`${SAILINGS_API}/${sailing._id}`, {
                                    method: "PUT",
                                    headers: { "Content-Type": "application/json" },
                                    body: JSON.stringify({ complianceChecklist: newChecklist }),
                                  });
                                  if (!res.ok) throw new Error("Failed to update compliance checklist");
                                  await loadData();
                                } catch (err) { alert(err.message); }
                              }}
                              className="h-4 w-4 rounded border-slate-300"
                            />
                            <div className="flex-1">
                              <div className="text-sm font-semibold text-slate-800">{item.label}</div>
                              <div className="text-xs text-slate-400">{item.ref}</div>
                            </div>
                            <span className={`text-xs font-bold ${checked ? "text-emerald-600" : "text-slate-400"}`}>{checked ? "✓ Done" : "Pending"}</span>
                          </label>
                        );
                      })}
                      <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                        <span className={`h-4 w-4 rounded-full flex items-center justify-center text-[10px] ${sailing.loadPlan?.status === "approved" ? "bg-emerald-500 text-white" : "bg-slate-300 text-white"}`}>{sailing.loadPlan?.status === "approved" ? "✓" : ""}</span>
                        <div className="flex-1">
                          <div className="text-sm font-semibold text-slate-800">Load Plan Approved</div>
                          <div className="text-xs text-slate-400">Set automatically when the Load Plan is approved below — not manually toggled here.</div>
                        </div>
                        <span className={`text-xs font-bold ${sailing.loadPlan?.status === "approved" ? "text-emerald-600" : "text-slate-400"}`}>{sailing.loadPlan?.status === "approved" ? "✓ Done" : "Pending"}</span>
                      </div>
                    </div>
                  </div>

                  {/* ── LIVESTOCK LOAD PLAN ── */}
                  <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                    <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-3">
                      <div>
                        <h2 className="text-base font-semibold text-slate-900">Livestock Load Plan</h2>
                        <p className="text-xs text-slate-400 mt-0.5">
                          {totals.livestockHeads} head booked · {vessel?.zones?.length ?? 0} pens configured · {" "}
                          <span className={`font-semibold ${sailing.loadPlan?.status === "approved" ? "text-emerald-600" : "text-amber-600"}`}>
                            {sailing.loadPlan?.status === "approved" ? "Approved" : sailing.loadPlan?.status === "draft" ? "Draft" : "Not Generated"}
                          </span>
                        </p>
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        <button
                          onClick={() => window.open(`/dashboard/sailings/${sailing._id}/loadplan`, "_blank")}
                          className="rounded-xl border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                        >
                          Print Load Plan
                        </button>
                        {sailing.loadPlan?.status === "approved" && (
                          <button
                            onClick={async () => {
                              try {
                                await fetch(`${SAILINGS_API}/${sailing._id}/loadplan`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ status: "draft" }) });
                                await loadData();
                              } catch (err) { alert(err.message); }
                            }}
                            className="rounded-xl border border-amber-200 bg-white px-3 py-1.5 text-xs font-semibold text-amber-600 hover:bg-amber-50"
                          >
                            Undo Approval
                          </button>
                        )}
                        {!vessel?.zones?.length ? null : sailing.loadPlan?.status !== "approved" && (
                          <button
                            disabled={generatingPlan}
                            onClick={async () => {
                              if (!vessel?.zones?.length) return alert("No pen layout configured on vessel.");
                              setGeneratingPlan(true);
                              try {
                                const livestockBookings = bookings.filter(b => (b.bookingType === "livestock" || b.bookingType === "mixed") && b.status !== "cancelled");
                                if (!livestockBookings.length) { alert("No livestock bookings to allocate."); return; }
                                const consignments = [];
                                for (const booking of livestockBookings) {
                                  for (const item of (booking.livestockItems || [])) {
                                    if (!item.headCount) continue;
                                    consignments.push({ bookingId: booking._id, customerName: booking.customerName || "—", animalClass: item.animalClass || item.livestockType || "cattle", avgWeightKg: Number(item.averageWeightKg || 450), headCount: Number(item.headCount || 0) });
                                  }
                                }
                                // T-Ranch loads first for this sailing (their cattle
                                // arrives at port first) — processed before any other
                                // customer, so their consignment lands in the pens
                                // that fill first (S11/P11 working back). This is a
                                // direct fix for this specific priority; a proper
                                // manual reordering control for arbitrary customers
                                // is a separate future task.
                                consignments.sort((a, b) => {
                                  const aFirst = /t-?ranch/i.test(a.customerName) ? 0 : 1;
                                  const bFirst = /t-?ranch/i.test(b.customerName) ? 0 : 1;
                                  return aFirst - bFirst;
                                });
                                // Fill order matches the confirmed real loading
                                // sequence — descending from the highest regular
                                // pen number, Starboard then Port at each number
                                // (S11, P11, S10, P10, ... S1, P1) — not just
                                // whatever order pens happen to be stored in.
                                // This means one customer's consignment naturally
                                // lands in pens that get loaded back-to-back on
                                // the day, rather than scattered across the
                                // storage order.
                                const loadingSeqPens = vessel.zones.filter(z => !z.isHospitalPen && !z.isSpare && z.type !== 'freight');
                                const otherPens = vessel.zones.filter(z => z.isHospitalPen || z.isSpare || z.type === 'freight');
                                const penNumVal = (z) => parseInt(String(z.penNumber || "").replace(/\D/g, ""), 10) || 0;
                                const maxPenNum = Math.max(0, ...loadingSeqPens.map(penNumVal));
                                const orderedLoadingPens = [];
                                for (let n = maxPenNum; n >= 1; n--) {
                                  const sZone = loadingSeqPens.find(z => (z.row === "starboard" || z.penNumber?.startsWith("S")) && penNumVal(z) === n);
                                  const pZone = loadingSeqPens.find(z => (z.row === "port" || z.penNumber?.startsWith("P")) && penNumVal(z) === n);
                                  if (sZone) orderedLoadingPens.push(sZone);
                                  if (pZone) orderedLoadingPens.push(pZone);
                                }
                                const sortedZones = [...orderedLoadingPens, ...otherPens];
                                const penPool = sortedZones.map(pen => ({ ...pen, allocations: [], currentHead: 0 }));
                                for (const consignment of consignments) {
                                  const minAreaPerHead = getMpiMinArea(consignment.avgWeightKg);
                                  let remaining = consignment.headCount;
                                  const classMatchPens = penPool.filter(p => !p.isHospitalPen && !p.isSpare && p.type !== 'freight' && p.allocations.some(a => a.animalClass === consignment.animalClass));
                                  const emptyPens = penPool.filter(p => !p.isHospitalPen && !p.isSpare && p.type !== 'freight' && p.allocations.length === 0);
                                  for (const pen of [...classMatchPens, ...emptyPens]) {
                                    if (remaining <= 0) break;
                                    const penArea = Number(pen.areaM2 || 0);
                                    const mpiMax = penArea ? Math.floor(penArea / minAreaPerHead) : (pen.maxHeadCount || 12);
                                    const space = mpiMax - pen.currentHead;
                                    if (space <= 0) continue;
                                    const toLoad = Math.min(remaining, space);
                                    pen.allocations.push({ bookingId: consignment.bookingId, customerName: consignment.customerName, animalClass: consignment.animalClass, avgWeightKg: consignment.avgWeightKg, headCount: toLoad, mpiMinAreaPerHead: minAreaPerHead, mpiMaxHead: mpiMax });
                                    pen.currentHead += toLoad;
                                    remaining -= toLoad;
                                  }
                                }
                                const totalHead = consignments.reduce((s, c) => s + c.headCount, 0);
                                const weightedAvg = totalHead > 0 ? consignments.reduce((s, c) => s + c.avgWeightKg * c.headCount, 0) / totalHead : 450;
                                const loadPlan = {
                                  status: "draft", avgWeightKg: Math.round(weightedAvg), mpiMinAreaPerHead: getMpiMinArea(weightedAvg),
                                  pens: penPool.map(p => {
                                    // Use the actual MPI-derived max head that was computed and
                                    // used to fill THIS pen's real animals during allocation above
                                    // (stored per-allocation as mpiMaxHead) — not a single
                                    // sailing-wide weighted average recalculated afterward, which
                                    // can differ from what these specific animals require and make
                                    // a correctly-filled pen appear over capacity. Take the minimum
                                    // across allocations if a pen ever holds more than one class,
                                    // to stay on the safe/conservative side.
                                    const allocatedMpiMax = p.allocations.length
                                      ? Math.min(...p.allocations.map(a => a.mpiMaxHead).filter(v => Number.isFinite(v)))
                                      : null;
                                    const maxHead = Number.isFinite(allocatedMpiMax)
                                      ? allocatedMpiMax
                                      : (p.areaM2 ? getMpiMaxHead(p.areaM2, weightedAvg) : (p.maxHeadCount || 0));
                                    return { penNumber: p.penNumber || p.name, penLabel: p.name, isHospitalPen: p.isHospitalPen || false, isContainerSlot: p.type === 'freight', maxTEU: p.maxTEU || 0, areaM2: Number(p.areaM2 || 0), maxHead, allocations: p.allocations };
                                  }),
                                };
                                const res = await fetch(`${SAILINGS_API}/${sailing._id}/loadplan`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(loadPlan) });
                                if (!res.ok) throw new Error("Failed to save load plan");
                                await loadData();
                              } catch (err) { alert(err.message); }
                              finally { setGeneratingPlan(false); }
                            }}
                            className="rounded-xl border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                          >
                            {generatingPlan ? "Generating..." : "Auto Generate"}
                          </button>
                        )}
                        {!vessel?.zones?.length ? null : sailing.loadPlan?.status !== "approved" && (sailing.loadPlan?.pens || []).some(p => p.allocations?.length > 0) && (
                          <button
                            onClick={async () => {
                              if (!window.confirm("This will clear all current pen allocations on this load plan. Continue?")) return;
                              try {
                                const clearedPlan = {
                                  ...sailing.loadPlan,
                                  pens: (sailing.loadPlan?.pens || []).map(p => ({ ...p, allocations: [] })),
                                };
                                const res = await fetch(`${SAILINGS_API}/${sailing._id}/loadplan`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(clearedPlan) });
                                if (!res.ok) throw new Error("Failed to clear load plan");
                                await loadData();
                              } catch (err) { alert(err.message); }
                            }}
                            className="rounded-xl border border-red-300 bg-white px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50"
                          >
                            Clear Load Plan
                          </button>
                        )}
                        {sailing.loadPlan?.status === "draft" && (
                          <button
                            onClick={async () => {
                              try {
                                await fetch(`${SAILINGS_API}/${sailing._id}/loadplan`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ status: "approved", approvedAt: new Date() }) });
                                await loadData();
                              } catch (err) { alert(err.message); }
                            }}
                            className="rounded-xl bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700"
                          >
                            Approve Plan
                          </button>
                        )}
                      </div>
                    </div>

                    {!vessel || !vessel.zones?.length ? (
                      <div className="px-5 py-10 text-center">
                        <div className="text-sm font-semibold text-slate-500 mb-1">No pen layout configured</div>
                        <p className="text-xs text-slate-400 mb-4">Configure vessel pens in Settings → Vessels first.</p>
                        <Link to="/dashboard/settings" className="text-xs font-semibold text-slate-700 underline">Go to Settings</Link>
                      </div>
                    ) : totals.livestockHeads === 0 ? (
                      <div className="px-5 py-10 text-center text-sm text-slate-400">
                        No livestock bookings on this sailing yet. The pen diagram will appear once livestock bookings are added.
                      </div>
                    ) : (
                      <div className="p-5 space-y-5">

                        {/* Vessel SVG diagram */}
                        <LoadingVesselDiagram
                          pens={sailing.loadPlan?.pens || vessel.zones.map(z => ({ penNumber: z.penNumber, penLabel: z.name, isHospitalPen: z.isHospitalPen, isContainerSlot: z.type === 'freight', maxTEU: z.maxTEU || 0, maxHead: z.maxHeadCount, areaM2: z.areaM2, allocations: [] }))}
                          vessel={vessel}
                          onPenClick={(pen) => { setSelectedPen(pen); setMoveAllocIndex(0); setMoveHeadCount(""); }}
                        />

                        {/* Allocation summary — only allocated pens */}
                        {sailing.loadPlan?.pens?.some(p => p.allocations?.length > 0) && (
                          <AllocationSummary pens={sailing.loadPlan.pens} />
                        )}

                        {/* Manual assign prompt if no plan */}
                        {!sailing.loadPlan?.pens?.length && (
                          <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm text-slate-400">
                            Click a pen to manually assign livestock, or use Auto Generate above.
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* ── FREIGHT SECTION ── */}
                  <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                    <div className="px-5 py-4 border-b border-slate-100">
                      <h2 className="text-base font-semibold text-slate-900">Freight & Cargo</h2>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {bookings.filter(b => b.bookingType === "freight" || b.bookingType === "machinery").length} freight/machinery bookings
                      </p>
                    </div>
                    {bookings.filter(b => b.bookingType === "freight" || b.bookingType === "machinery" || b.bookingType === "mixed").length === 0 ? (
                      <div className="px-5 py-8 text-center text-sm text-slate-400">No freight or machinery bookings on this sailing.</div>
                    ) : (
                      <div className="p-5 space-y-4">
                        {/* Freight vessel diagram */}
                        <FreightDeckDiagram bookings={bookings.filter(b => b.bookingType === "freight" || b.bookingType === "machinery" || b.bookingType === "mixed")} />

                        {/* Freight booking list */}
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-slate-100">
                              <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">Ref</th>
                              <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">Customer</th>
                              <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">Type</th>
                              <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">Description</th>
                              <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">Weight</th>
                              <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {bookings.filter(b => b.bookingType === "freight" || b.bookingType === "machinery" || b.bookingType === "mixed").map((b, i) => {
                              const items = b.freightItems || [];
                              const totalWeight = items.reduce((s, item) => s + Number(item.weightKg || 0), 0);
                              return (
                                <tr key={b._id} className={`hover:bg-slate-50 cursor-pointer ${i > 0 ? "border-t border-slate-100" : ""}`} onClick={() => navigate(`/dashboard/bookings/${b._id}`)}>
                                  <td className="px-3 py-2.5 font-semibold text-xs text-slate-900">{b.bookingRef || b._id?.slice(-6).toUpperCase()}</td>
                                  <td className="px-3 py-2.5 text-slate-700">{b.customerName || "—"}</td>
                                  <td className="px-3 py-2.5 text-xs text-slate-500 capitalize">{b.bookingType}</td>
                                  <td className="px-3 py-2.5 text-xs text-slate-500">{items[0]?.description || b.machinery?.description || "—"}</td>
                                  <td className="px-3 py-2.5 text-xs text-slate-500">{totalWeight > 0 ? `${totalWeight.toLocaleString()} kg` : "—"}</td>
                                  <td className="px-3 py-2.5"><StatusBadge status={b.status} /></td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                </div>
              )}


              {/* DOCUMENTS TAB */}
              {activeTab === "documents" && (
                <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                  <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                    <div>
                      <h2 className="text-base font-semibold text-slate-900">Documents</h2>
                      <p className="text-sm text-slate-400 mt-0.5">Manifests, logs, vet certificates, photos and voyage documents.</p>
                    </div>
                    <label className={`rounded-xl bg-slate-950 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 cursor-pointer ${uploading ? "opacity-50 pointer-events-none" : ""}`}>
                      {uploading ? "Uploading..." : "+ Upload"}
                      <input type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.xlsx,.xls,.csv,.doc,.docx" className="hidden"
                        disabled={uploading}
                        onChange={async (e) => {
                          const files = Array.from(e.target.files);
                          if (!files.length) return;
                          setUploading(true);
                          for (const file of files) {
                            const fd = new FormData();
                            fd.append("images", file);
                            try {
                              const res = await fetch(`${API_BASE}/upload/images`, { method: "POST", body: fd });
                              if (!res.ok) throw new Error("Upload failed");
                              const url = await res.json();
                              const token = localStorage.getItem("token");
                              const updated = [...(sailing.documents || []), {
                                name: file.name,
                                url: typeof url === "string" ? url : "",
                                uploadedAt: new Date().toISOString(),
                                type: file.type,
                              }];
                              // Try dedicated endpoint first, fall back to full PUT
                              const patchRes = await fetch(`${SAILINGS_API}/${sailing._id}/documents`, {
                                method: "PATCH",
                                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                                body: JSON.stringify({ documents: updated }),
                              });
                              if (!patchRes.ok) {
                                // fallback to full PUT
                                await fetch(`${SAILINGS_API}/${sailing._id}`, {
                                  method: "PUT",
                                  headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                                  body: JSON.stringify({ documents: updated }),
                                });
                              }
                              await loadData();
                            } catch (err) {
                              console.error("Upload error:", err);
                              alert(`Failed to upload ${file.name}`);
                            }
                          }
                          setUploading(false);
                          e.target.value = "";
                        }}
                      />
                    </label>
                  </div>
                  {!(sailing.documents?.length) ? (
                    <div className="px-5 py-12 text-center">
                      <div className="text-sm font-semibold text-slate-500 mb-1">No documents uploaded</div>
                      <div className="text-xs text-slate-400">Upload manifests, vet certificates, voyage logs and photos.</div>
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-[1fr_120px_80px_60px] gap-3 border-b border-slate-100 px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">
                        <div>File</div><div>Uploaded</div><div>Type</div><div></div>
                      </div>
                      {(sailing.documents || []).map((doc, i) => {
                        const ext = doc.name?.split(".").pop()?.toLowerCase();
                        return (
                          <div key={i} className={`grid grid-cols-[1fr_120px_80px_60px] gap-3 px-5 py-3.5 text-sm items-center hover:bg-slate-50 transition ${i > 0 ? "border-t border-slate-100" : ""}`}>
                            <div className="font-medium text-slate-900 truncate">{doc.name}</div>
                            <div className="text-xs text-slate-400">{doc.uploadedAt ? new Date(doc.uploadedAt).toLocaleDateString("en-NZ") : "—"}</div>
                            <div className="text-xs text-slate-400 uppercase">{ext || "—"}</div>
                            <a href={doc.url} target="_blank" rel="noreferrer" className="text-xs font-semibold text-slate-900 hover:underline">View</a>
                          </div>
                        );
                      })}
                    </>
                  )}
                </div>
              )}


            </div>

          </div>

            {/* ── Sidebar ── */}
            <div className="space-y-4">

              {/* Sailing Summary */}
              <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-100">
                  <h3 className="text-sm font-semibold text-slate-900">Sailing Summary</h3>
                </div>
                <div className="px-4 py-3 space-y-3">
                  {[
                    { label: "Sailing Ref", value: sailingRef || "—" },
                    { label: "Route", value: route || "—" },
                    { label: "Vessel", value: sailing?.vessel?.name || sailing?.vesselName || "—" },
                    { label: "Status", value: toTitle(sailing?.status || "draft") },
                  ].map(item => (
                    <div key={item.label} className="flex items-start justify-between gap-2">
                      <span className="text-xs text-slate-400 shrink-0">{item.label}</span>
                      <span className="text-xs font-semibold text-slate-900 text-right">{item.value}</span>
                    </div>
                  ))}

                  <div className="border-t border-slate-100 pt-3 space-y-3">
                    <EditableDateRow label="Booking Cut-off" value={sailing?.bookingCutoff} onSave={(v) => saveSailingDateField("bookingCutoff", v)} />
                    <EditableDateRow label="ETD" value={sailing?.etd} onSave={(v) => saveSailingDateField("etd", v)} />
                    <EditableDateRow label="ETA" value={sailing?.eta} onSave={(v) => saveSailingDateField("eta", v)} />
                    {[
                      { label: "Loading Started", value: formatDate(sailing?.loadingStartedAt) },
                      { label: "Departed", value: formatDate(sailing?.departedAt) },
                      { label: "Arrived", value: formatDate(sailing?.arrivedAt) },
                      { label: "Closed", value: formatDate(sailing?.closedAt) },
                    ].map(item => (
                      <div key={item.label} className="flex items-start justify-between gap-2">
                        <span className="text-xs text-slate-400 shrink-0">{item.label}</span>
                        <span className="text-xs font-medium text-slate-700 text-right">{item.value || "—"}</span>
                      </div>
                    ))}
                  </div>

                  <div className="border-t border-slate-100 pt-3 space-y-3">
                    {[
                      { label: "Bookings", value: manifestedBookings.length },
                      { label: "Livestock Head", value: `${totals.livestockHeads} hd` },
                      { label: "Total Weight", value: `${totals.weightKg.toLocaleString()} kg` },
                      { label: "Manifest", value: manifestLocked ? "Locked" : "Open" },
                      { label: "Load Plan", value: toTitle(sailing?.loadPlan?.status || "none") },
                    ].map(item => (
                      <div key={item.label} className="flex items-start justify-between gap-2">
                        <span className="text-xs text-slate-400 shrink-0">{item.label}</span>
                        <span className="text-xs font-semibold text-slate-900 text-right">{item.value}</span>
                      </div>
                    ))}
                  </div>

                </div>
              </div>

              {/* Quick actions */}
              <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-100">
                  <h3 className="text-sm font-semibold text-slate-900">Quick Actions</h3>
                </div>
                <div className="px-4 py-3 space-y-2">
                  <Link to={`/dashboard/bookings/new?sailingId=${sailing._id}`}
                    className="block w-full text-center rounded-xl border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition">
                    + Add Booking
                  </Link>
                  <Link to={`/dashboard/sailings/${sailing._id}/edit`}
                    className="block w-full text-center rounded-xl border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition">
                    Edit Sailing
                  </Link>
                  <button
                    onClick={() => window.open(`/dashboard/sailings/${sailing._id}/manifest/print`, "_blank")}
                    className="block w-full text-center rounded-xl border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition">
                    Print Manifest
                  </button>
                  <button
                    onClick={() => window.open(`/dashboard/sailings/${sailing._id}/loadplan`, "_blank")}
                    className="block w-full text-center rounded-xl border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition">
                    Print Load Plan
                  </button>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>

      {/* ── Print view ── */}
      <div className="print-only">
        {printMode === "loadplan" ? (
          <LoadPlanReport
            sailing={sailing}
            vessel={vessel}
            bookings={bookings}
            pens={sailing.loadPlan?.pens || []}
          />
        ) : (
          <ManifestReport
            sailing={sailing}
            sailingRef={sailingRef}
            route={route}
            manifestedBookings={manifestedBookings}
            totals={totals}
            savedManifest={savedManifest}
          />
        )}
      </div>

      <style>{`
        .print-only { display: none !important; }

        @media print {
          html, body {
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
            height: auto !important;
            overflow: visible !important;
          }
          /* An SPA shell commonly sets #root (and often a wrapper div or
             two beneath it) to height:100vh + overflow:hidden for normal
             in-app scrolling. That clips print content to one viewport's
             worth regardless of .print-only's own positioning — which is
             exactly what kept truncating this table at a fixed row count
             no matter what was fixed on .print-only alone. Force every
             ancestor open rather than guessing the exact wrapper depth. */
          #root, #root > *, #root > * > *, #root > * > * > * {
            height: auto !important;
            max-height: none !important;
            overflow: visible !important;
          }
          body * { visibility: hidden !important; }
          .print-only, .print-only * { visibility: visible !important; }
          .print-only {
            position: static !important;
            width: 100% !important;
            height: auto !important;
            max-height: none !important;
            overflow: visible !important;
            background: white !important;
            padding: 0 !important;
            margin: 0 !important;
            display: block !important;
          }
          .no-print { display: none !important; }
          @page { size: A4 landscape; margin: 8mm; }
          .print-section tr { break-inside: avoid; page-break-inside: avoid; }
        }
      `}</style>
    </>
  );
}

function ManifestWorkflowTable({ bookings, saving, onStatusChange }) {
  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-950">Loading Workflow</h3>
        <span className="text-sm text-slate-500">{bookings.length} bookings</span>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-slate-200">
        <table className="w-full text-sm">
          <thead className="border-b border-slate-200 bg-slate-50">
            <tr>
              <th className="px-4 py-3 text-left font-semibold text-slate-700">Ref</th>
              <th className="px-4 py-3 text-left font-semibold text-slate-700">Customer</th>
              <th className="px-4 py-3 text-left font-semibold text-slate-700">Type</th>
              <th className="px-4 py-3 text-left font-semibold text-slate-700">Manifest Status</th>
              <th className="px-4 py-3 text-left font-semibold text-slate-700">Weight</th>
              <th className="px-4 py-3 text-right font-semibold text-slate-700">Actions</th>
            </tr>
          </thead>
          <tbody>
            {bookings.length === 0 ? (
              <tr>
                <td colSpan="6" className="px-4 py-8 text-center text-slate-500">
                  No bookings assigned to this sailing.
                </td>
              </tr>
            ) : (
              bookings.map((booking) => {
                const ref =
                  booking.bookingRef ||
                  booking.ref ||
                  booking._id?.slice(-6).toUpperCase();
                const manifestStatus = booking.manifestStatus || "Pending";
                return (
                  <tr key={booking._id || ref} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3 font-semibold text-slate-900">{ref}</td>
                    <td className="px-4 py-3 text-slate-700">{booking.customerName || "Unknown"}</td>
                    <td className="px-4 py-3 text-slate-700">{toTitle(booking.bookingType || "booking")}</td>
                    <td className="px-4 py-3">
                      <StatusBadge status={manifestStatus} />
                    </td>
                    <td className="px-4 py-3 text-slate-700">
                      {Number(booking?.totals?.weightKg || 0).toLocaleString()} kg
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap justify-end gap-1.5">
                        {["Confirmed", "Loaded", "Short Shipped", "Offloaded"].map((status) => (
                          <button
                            key={status}
                            type="button"
                            disabled={saving}
                            onClick={() => onStatusChange(booking._id, status)}
                            className="rounded-lg border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40"
                          >
                            {status}
                          </button>
                        ))}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BookingsTable({ bookings }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="border-b border-slate-200 bg-slate-50">
          <tr>
            <th className="px-4 py-3 text-left font-semibold text-slate-700">Ref</th>
            <th className="px-4 py-3 text-left font-semibold text-slate-700">Customer</th>
            <th className="px-4 py-3 text-left font-semibold text-slate-700">Type</th>
            <th className="px-4 py-3 text-left font-semibold text-slate-700">Status</th>
            <th className="px-4 py-3 text-left font-semibold text-slate-700">Manifest</th>
            <th className="px-4 py-3 text-left font-semibold text-slate-700">Revenue</th>
            <th className="px-4 py-3 text-right font-semibold text-slate-700">View</th>
          </tr>
        </thead>
        <tbody>
          {bookings.length === 0 ? (
            <tr>
              <td colSpan="7" className="px-4 py-8 text-center text-slate-500">
                No bookings linked to this sailing.
              </td>
            </tr>
          ) : (
            bookings.map((booking) => {
              const ref =
                booking.bookingRef ||
                booking.ref ||
                booking._id?.slice(-6).toUpperCase();
              const revenue = Number(
                booking?.charges?.total ||
                  booking?.pricingSnapshot?.calculatedTotal ||
                  booking?.totals?.estimatedRevenue ||
                  booking?.invoiceSnapshot?.total ||
                  0
              );
              return (
                <tr key={booking._id || ref} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 font-semibold text-slate-900">{ref}</td>
                  <td className="px-4 py-3 text-slate-700">{booking.customerName || "Unknown"}</td>
                  <td className="px-4 py-3 text-slate-700">{toTitle(booking.bookingType || "booking")}</td>
                  <td className="px-4 py-3"><StatusBadge status={booking.status} /></td>
                  <td className="px-4 py-3"><StatusBadge status={booking.manifestStatus || "Pending"} /></td>
                  <td className="px-4 py-3 text-slate-700">${revenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  <td className="px-4 py-3 text-right">
                    <Link
                      to={`/dashboard/bookings/${booking._id}`}
                      className="inline-block rounded-lg border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                    >
                      Open
                    </Link>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}

function ManifestTables({ manifestedBookings }) {
  return (
    <div className="space-y-8">
      <div>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-950">Livestock Manifest</h3>
          <span className="text-sm text-slate-500">
            {(manifestedBookings || []).reduce(
              (acc, booking) => acc + (booking.livestockItems || []).length,
              0
            )}{" "}
            line items
          </span>
        </div>
        <div className="overflow-x-auto rounded-2xl border border-slate-200">
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-slate-700">Booking</th>
                <th className="px-4 py-3 text-left font-semibold text-slate-700">Customer</th>
                <th className="px-4 py-3 text-left font-semibold text-slate-700">Type</th>
                <th className="px-4 py-3 text-left font-semibold text-slate-700">Heads</th>
                <th className="px-4 py-3 text-left font-semibold text-slate-700">Avg Weight</th>
                <th className="px-4 py-3 text-left font-semibold text-slate-700">Total Weight</th>
              </tr>
            </thead>
            <tbody>
              {manifestedBookings.flatMap((booking) =>
                (booking.livestockItems || []).map((item, index) => {
                  const heads = Number(item.headCount || 0);
                  const avgWeight = Number(item.averageWeightKg || 0);
                  return (
                    <tr
                      key={(booking._id || booking.bookingRef || "booking") + "-livestock-" + index}
                      className="border-b border-slate-100 hover:bg-slate-50"
                    >
                      <td className="px-4 py-3 font-semibold">{booking.bookingRef || booking.ref || "-"}</td>
                      <td className="px-4 py-3">{booking.customerName || "Unknown"}</td>
                      <td className="px-4 py-3">{item.livestockType || "Livestock"}</td>
                      <td className="px-4 py-3">{heads.toLocaleString()}</td>
                      <td className="px-4 py-3">{avgWeight} kg</td>
                      <td className="px-4 py-3 font-semibold">{(heads * avgWeight).toLocaleString()} kg</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-950">Freight Manifest</h3>
          <span className="text-sm text-slate-500">
            {(manifestedBookings || []).reduce(
              (acc, booking) => acc + (booking.freightItems || []).length,
              0
            )}{" "}
            line items
          </span>
        </div>
        <div className="overflow-x-auto rounded-2xl border border-slate-200">
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-slate-700">Booking</th>
                <th className="px-4 py-3 text-left font-semibold text-slate-700">Customer</th>
                <th className="px-4 py-3 text-left font-semibold text-slate-700">Freight Type</th>
                <th className="px-4 py-3 text-left font-semibold text-slate-700">Qty</th>
                <th className="px-4 py-3 text-left font-semibold text-slate-700">Weight</th>
                <th className="px-4 py-3 text-left font-semibold text-slate-700">Cube</th>
              </tr>
            </thead>
            <tbody>
              {manifestedBookings.flatMap((booking) =>
                (booking.freightItems || []).map((item, index) => (
                  <tr
                    key={(booking._id || booking.bookingRef || "booking") + "-freight-" + index}
                    className="border-b border-slate-100 hover:bg-slate-50"
                  >
                    <td className="px-4 py-3 font-semibold">{booking.bookingRef || booking.ref || "-"}</td>
                    <td className="px-4 py-3">{booking.customerName || "Unknown"}</td>
                    <td className="px-4 py-3">{item.freightType || item.description || "Freight"}</td>
                    <td className="px-4 py-3">{item.quantity || "-"}</td>
                    <td className="px-4 py-3">{Number(item.weightKg || 0).toLocaleString()} kg</td>
                    <td className="px-4 py-3 font-semibold">{Number(item.cubeM3 || 0).toLocaleString()} m³</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ManifestReport({ sailing, sailingRef, route, manifestedBookings, totals, savedManifest }) {
  return (
    <div style={{ fontFamily: "Arial, sans-serif", fontSize: "11px", color: "#000", padding: "8px" }}>
      <h1 style={{ textAlign: "center", fontSize: "20px", margin: 0 }}>44 SOUTH SHIPPING</h1>
      <h2 style={{ textAlign: "center", fontSize: "16px", marginTop: "4px" }}>VOYAGE MANIFEST REPORT</h2>

      <table style={printTableStyle}>
        <tbody>
          <tr>
            <PrintCell label="Manifest No" value={savedManifest?.manifestNumber || "Live"} />
            <PrintCell label="Sailing Ref" value={sailingRef} />
            <PrintCell label="Vessel" value={sailing?.vessel?.name || sailing?.vesselName || "-"} />
          </tr>
          <tr>
            <PrintCell label="Route" value={route} />
            <PrintCell label="ETD" value={formatDate(sailing?.etd)} />
            <PrintCell label="ETA" value={formatDate(sailing?.eta)} />
          </tr>
          <tr>
            <PrintCell label="Status" value={toTitle(sailing?.status || "draft")} />
            <PrintCell label="Generated" value={formatDate(new Date())} />
            <PrintCell label="Bookings" value={manifestedBookings.length} />
          </tr>
        </tbody>
      </table>

      <h3>Voyage Summary</h3>
      <table style={printTableStyle}>
        <tbody>
          <tr>
            <PrintCell label="Total Bookings" value={manifestedBookings.length} />
            <PrintCell label="Livestock Heads" value={totals.livestockHeads} />
            <PrintCell label="Total Weight" value={Number(totals.weightKg || 0).toLocaleString() + " kg"} />
            <PrintCell label="Total Cube" value={Number(totals.freightCube || 0).toLocaleString() + " m³"} />
          </tr>
        </tbody>
      </table>

      <h3>Livestock Manifest</h3>
      <table style={printTableStyle}>
        <thead>
          <tr>
            <PrintTh>Booking</PrintTh>
            <PrintTh>Customer</PrintTh>
            <PrintTh>Type</PrintTh>
            <PrintTh>Heads</PrintTh>
            <PrintTh>Avg Weight</PrintTh>
            <PrintTh>Total Weight</PrintTh>
          </tr>
        </thead>
        <tbody>
          {manifestedBookings.flatMap((booking) =>
            (booking.livestockItems || []).map((item, index) => {
              const heads = Number(item.headCount || 0);
              const avg = Number(item.averageWeightKg || 0);
              return (
                <tr key={(booking._id || booking.bookingRef || "booking") + "-print-live-" + index}>
                  <PrintTd>{booking.bookingRef || booking.ref || "-"}</PrintTd>
                  <PrintTd>{booking.customerName || "Unknown"}</PrintTd>
                  <PrintTd>{item.livestockType || "Livestock"}</PrintTd>
                  <PrintTd>{heads}</PrintTd>
                  <PrintTd>{avg} kg</PrintTd>
                  <PrintTd>{(heads * avg).toLocaleString()} kg</PrintTd>
                </tr>
              );
            })
          )}
        </tbody>
      </table>

      <h3>Freight Manifest</h3>
      <table style={printTableStyle}>
        <thead>
          <tr>
            <PrintTh>Booking</PrintTh>
            <PrintTh>Customer</PrintTh>
            <PrintTh>Freight Type</PrintTh>
            <PrintTh>Qty</PrintTh>
            <PrintTh>Weight</PrintTh>
            <PrintTh>Cube</PrintTh>
          </tr>
        </thead>
        <tbody>
          {manifestedBookings.flatMap((booking) =>
            (booking.freightItems || []).map((item, index) => (
              <tr key={(booking._id || booking.bookingRef || "booking") + "-print-freight-" + index}>
                <PrintTd>{booking.bookingRef || booking.ref || "-"}</PrintTd>
                <PrintTd>{booking.customerName || "Unknown"}</PrintTd>
                <PrintTd>{item.freightType || item.description || "Freight"}</PrintTd>
                <PrintTd>{item.quantity || "-"}</PrintTd>
                <PrintTd>{Number(item.weightKg || 0).toLocaleString()} kg</PrintTd>
                <PrintTd>{Number(item.cubeM3 || 0).toLocaleString()} m³</PrintTd>
              </tr>
            ))
          )}
        </tbody>
      </table>

      <h3>Sign Off</h3>
      <table style={printTableStyle}>
        <tbody>
          <tr>
            <PrintTd>Prepared By</PrintTd>
            <PrintTd></PrintTd>
            <PrintTd>Approved By</PrintTd>
            <PrintTd></PrintTd>
            <PrintTd>Master / Skipper</PrintTd>
            <PrintTd></PrintTd>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

const printTableStyle = { width: "100%", borderCollapse: "collapse", marginBottom: "12px" };

function PrintCell({ label, value }) {
  return (
    <td style={{ border: "1px solid #000", padding: "6px" }}>
      <strong>{label}: </strong>{value || "-"}
    </td>
  );
}

function PrintTh({ children }) {
  return (
    <th style={{ border: "1px solid #000", padding: "6px", background: "#f1f5f9", textAlign: "left" }}>
      {children}
    </th>
  );
}

function PrintTd({ children }) {
  return <td style={{ border: "1px solid #000", padding: "6px" }}>{children}</td>;
}

function StatusBadge({ status }) {
  const clean = String(status || "pending").toLowerCase();
  const cls =
    clean === "open" ? "bg-emerald-100 text-emerald-700"
    : clean === "confirmed" ? "bg-sky-100 text-sky-700"
    : clean === "loaded" ? "bg-emerald-100 text-emerald-700"
    : clean === "offloaded" ? "bg-blue-100 text-blue-700"
    : clean === "short shipped" ? "bg-orange-100 text-orange-700"
    : clean === "closed" ? "bg-slate-100 text-slate-700"
    : clean === "departed" ? "bg-purple-100 text-purple-700"
    : clean === "arrived" ? "bg-blue-100 text-blue-700"
    : clean === "completed" ? "bg-emerald-100 text-emerald-700"
    : clean === "cancelled" ? "bg-red-100 text-red-700"
    : "bg-amber-100 text-amber-700";
  return (
    <span className={"inline-flex rounded-full px-3 py-1 text-xs font-medium " + cls}>
      {toTitle(clean)}
    </span>
  );
}

function MetricCard({ label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="text-xs font-medium uppercase tracking-wide text-slate-400">{label}</div>
      <div className="mt-2 text-2xl font-bold text-slate-950">{value}</div>
    </div>
  );
}

function SectionCard({ title, action, children }) {
  return (
    <div className="rounded-3xl border border-slate-200 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
        <h2 className="font-semibold text-slate-950">{title}</h2>
        {action}
      </div>
      <div className="p-6">{children}</div>
    </div>
  );
}

function AsideCard({ title, children }) {
  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
      <h2 className="mb-4 text-xs font-semibold uppercase tracking-[0.15em] text-slate-400">{title}</h2>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-sm text-slate-500">{label}</span>
      <span className="text-right text-sm font-semibold text-slate-900">{value || "-"}</span>
    </div>
  );
}

function SummaryTile({ label, value }) {
  return (
    <div className="rounded-2xl bg-slate-50 p-4">
      <div className="text-xs font-medium uppercase tracking-wide text-slate-400">{label}</div>
      <div className="mt-2 text-lg font-bold text-slate-950">{value}</div>
    </div>
  );
}

function CapacityBar({ label, value, total, percent, suffix = "" }) {
  const barColor =
    percent >= 90 ? "bg-red-500"
    : percent >= 70 ? "bg-amber-400"
    : "bg-emerald-500";

  return (
    <div>
      <div className="mb-2 flex items-center justify-between text-sm">
        <span className="font-medium text-slate-700">{label}</span>
        <span className="text-slate-500">
          {Number(value || 0).toLocaleString()}{suffix} / {Number(total || 0).toLocaleString()}{suffix}
          <span className="ml-2 font-semibold text-slate-700">{Math.round(percent)}%</span>
        </span>
      </div>
      <div className="h-2.5 overflow-hidden rounded-full bg-slate-100">
        <div
          className={`h-2.5 rounded-full transition-all ${barColor}`}
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
}


function LoadPlanReport({ sailing, vessel, bookings, pens }) {
  const MPI_TABLE = [
    [200,0.770],[210,0.804],[220,0.838],[230,0.872],[240,0.906],
    [250,0.940],[260,0.974],[270,1.008],[280,1.042],[290,1.076],
    [300,1.110],[310,1.144],[320,1.178],[330,1.212],[340,1.246],
    [350,1.280],[360,1.314],[370,1.348],[380,1.382],[390,1.416],
    [400,1.450],[410,1.478],[420,1.505],[430,1.533],[440,1.560],
    [450,1.588],[460,1.615],[470,1.643],[480,1.670],[490,1.698],
    [500,1.725],[510,1.753],[520,1.780],[530,1.808],[540,1.835],
    [550,1.863],[560,1.890],[570,1.918],[580,1.945],[590,1.973],
    [600,2.000],[650,2.150],[700,3.000],
  ];
  const getMinArea = (w) => {
    w = Number(w || 0);
    for (let i = MPI_TABLE.length - 1; i >= 0; i--) if (w >= MPI_TABLE[i][0]) return MPI_TABLE[i][1];
    return 0.770;
  };

  const plan = sailing.loadPlan || {};
  const checklist = sailing.complianceChecklist || {};
  const avgWeight = plan.avgWeightKg || 450;
  const mpiMinArea = plan.mpiMinAreaPerHead || getMinArea(avgWeight);
  const totalArea = (vessel?.zones || []).filter(z => !z.isHospitalPen && !z.isSpare && z.type !== 'freight').reduce((s, z) => s + Number(z.areaM2 || 0), 0);
  const mpiMaxHead = totalArea ? Math.floor(totalArea / mpiMinArea) : 0;
  const totalHead = pens.reduce((s, p) => s + (p.allocations?.reduce((ss, a) => ss + a.headCount, 0) || 0), 0);
  const totalWeight = pens.reduce((s, p) => s + (p.allocations?.reduce((ss, a) => ss + a.headCount * (a.avgWeightKg || 0), 0) || 0), 0);
  const complianceOk = totalHead <= mpiMaxHead;
  const fmt = (d) => d ? new Date(d).toLocaleDateString("en-NZ", { day: "2-digit", month: "short", year: "numeric" }) : "—";

  const s = { fontFamily: "Arial, Helvetica, sans-serif", fontSize: 10, color: "#1a1a1a" };
  const th = { padding: "5px 8px", background: "#1a1a1a", color: "#fff", fontSize: 9, textTransform: "uppercase", letterSpacing: "0.05em", textAlign: "left", fontWeight: 700 };
  const td = (bg) => ({ padding: "5px 8px", borderBottom: "1px solid #eee", background: bg || "#fff", verticalAlign: "top" });

  return (
    <div style={{ ...s, padding: "16px 20px", background: "#fff" }}>

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", borderBottom: "3px solid #7a1428", paddingBottom: 12, marginBottom: 14 }}>
        <div>
          <img src={mcbLogo} alt="McCallum Bros Ltd" style={{ height: 40, width: "auto" }} />
          <div style={{ fontSize: 9, color: "#888", marginTop: 4 }}>Established 1904 · 747 Rosebank Road, Avondale, Auckland · (09) 828 5202</div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 14, fontWeight: 800 }}>LIVESTOCK LOAD PLAN</div>
          <div style={{ fontSize: 9, color: "#888" }}>MNZ Rule Part 24c Compliant · Generated: {fmt(new Date())}</div>
        </div>
      </div>

      {/* Voyage info */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", border: "1px solid #ddd", marginBottom: 14, borderRadius: 3, overflow: "hidden" }}>
        {[
          { l: "Sailing Ref", v: sailing.sailingRef },
          { l: "Vessel", v: sailing.vesselName || vessel?.name },
          { l: "ETD", v: fmt(sailing.etd) },
          { l: "Status", v: toTitle(sailing.status) },
          { l: "Route", v: `${sailing.origin} → ${sailing.destination}` },
          { l: "Plan Status", v: toTitle(plan.status || "none") },
          { l: "Total Head", v: totalHead.toLocaleString() },
          { l: "Total Weight", v: `${Math.round(totalWeight).toLocaleString()} kg` },
        ].map((item, i) => (
          <div key={i} style={{ padding: "7px 10px", borderRight: i % 4 < 3 ? "1px solid #ddd" : "none", borderBottom: i < 4 ? "1px solid #ddd" : "none", background: Math.floor(i / 4) % 2 === 0 ? "#fafafa" : "#fff" }}>
            <div style={{ fontSize: 8, fontWeight: 700, textTransform: "uppercase", color: "#999", marginBottom: 2 }}>{item.l}</div>
            <div style={{ fontSize: 11, fontWeight: 600 }}>{item.v || "—"}</div>
          </div>
        ))}
      </div>

      {/* Pen allocation table */}
      <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "#7a1428", marginBottom: 6, borderBottom: "1px solid #eee", paddingBottom: 4 }}>Pen Allocation Summary</div>
      <table className="print-section" style={{ width: "100%", borderCollapse: "collapse", marginBottom: 14, fontSize: 10 }}>
        <thead>
          <tr>{["Pen","Side","Area m²","Customer","Class","Head","Avg Wt (kg)","MPI Min m²/hd","MPI Max","Cap %"].map(h => <th key={h} style={th}>{h}</th>)}</tr>
        </thead>
        <tbody>
          {pens.map((pen, pi) => {
            const zone = vessel?.zones?.find(z => z.penNumber === pen.penNumber || z.name === pen.penLabel);
            const side = zone?.row ? toTitle(zone.row) : pen.penNumber?.startsWith("P") ? "Port" : pen.penNumber?.startsWith("S") ? "Starboard" : "—";
            const area = Number(zone?.areaM2 || pen.areaM2 || 0);
            const rowBg = pi % 2 === 0 ? "#fff" : "#fafafa";
            if (pen.isContainerSlot) return null;
            if (pen.isHospitalPen) return (
              <tr key={pi}><td style={{ ...td("#fff5f5"), fontWeight: 700, color: "#c00" }}>{pen.penNumber}</td><td style={td("#fff5f5")}>{side}</td><td style={td("#fff5f5")}>{area||"—"}</td><td colSpan={7} style={{ ...td("#fff5f5"), color: "#c00", fontStyle: "italic" }}>HOSPITAL PEN — Reserved (MNZ Rule Part 24c)</td></tr>
            );
            if (!pen.allocations?.length) return (
              <tr key={pi}><td style={td(rowBg)}><strong>{pen.penNumber}</strong></td><td style={td(rowBg)}>{side}</td><td style={td(rowBg)}>{area||"—"}</td><td colSpan={7} style={{ ...td(rowBg), color: "#ccc", fontStyle: "italic" }}>Empty</td></tr>
            );
            const isSharedPen = pen.allocations.length > 1;
            const totalPenAreaUsed = pen.allocations.reduce((s, a) => s + a.headCount * getMinArea(a.avgWeightKg), 0);
            return pen.allocations.map((alloc, ai) => {
              const mpiA = getMinArea(alloc.avgWeightKg);
              const mpiMax = area ? Math.floor(area / mpiA) : pen.maxHead;
              // A pen shared by more than one allocation can't have each
              // one's percentage calculated against the FULL pen area
              // independently — that ignores the other allocation using
              // real space in the same pen. Show the pen's true combined
              // capacity percentage instead when it's shared.
              const pct = isSharedPen
                ? (area ? Math.round((totalPenAreaUsed / area) * 100) : 0)
                : (mpiMax > 0 ? Math.round((alloc.headCount / mpiMax) * 100) : 0);
              const pctColor = pct >= 95 ? "#c00" : pct >= 80 ? "#c07700" : "#007a3d";
              return (
                <tr key={`${pi}-${ai}`}>
                  {ai === 0 && <td rowSpan={pen.allocations.length} style={{ ...td(rowBg), fontWeight: 700 }}>{pen.penNumber}{isSharedPen && <div style={{ fontSize: 9, color: "#c00", fontWeight: 400, fontStyle: "italic" }}>mixed classes</div>}</td>}
                  {ai === 0 && <td rowSpan={pen.allocations.length} style={td(rowBg)}>{side}</td>}
                  {ai === 0 && <td rowSpan={pen.allocations.length} style={td(rowBg)}>{area||"—"}</td>}
                  <td style={td(rowBg)}>{alloc.customerName}</td>
                  <td style={{ ...td(rowBg), textTransform: "capitalize" }}>{alloc.animalClass||"—"}</td>
                  <td style={{ ...td(rowBg), fontWeight: 700 }}>{alloc.headCount}</td>
                  <td style={td(rowBg)}>{alloc.avgWeightKg||"—"}</td>
                  <td style={td(rowBg)}>{mpiA}</td>
                  <td style={td(rowBg)}>{isSharedPen ? "—" : mpiMax}</td>
                  <td style={{ ...td(rowBg), fontWeight: 700, color: pctColor }}>{pct}%</td>
                </tr>
              );
            });
          })}
          <tr style={{ background: "#1a1a1a", color: "#fff", fontWeight: 700 }}>
            <td colSpan={5} style={{ padding: "6px 8px", fontSize: 10 }}>TOTAL</td>
            <td style={{ padding: "6px 8px", fontSize: 11 }}>{totalHead}</td>
            <td colSpan={2} style={{ padding: "6px 8px", fontSize: 10 }}>MPI Limit: {mpiMaxHead}</td>
            <td colSpan={2} style={{ padding: "6px 8px", fontSize: 10, color: complianceOk ? "#4ade80" : "#f87171" }}>{complianceOk ? "✓ COMPLIANT" : "✗ EXCEEDS LIMIT"}</td>
          </tr>
        </tbody>
      </table>

      {/* MPI density + compliance checklist side by side */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", color: "#7a1428", marginBottom: 6, borderBottom: "1px solid #eee", paddingBottom: 4 }}>MPI Density — Table 4 (MNZ Rule Part 24c)</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", border: "1px solid #ddd", borderRadius: 3, overflow: "hidden" }}>
            {[
              { l: "Avg Weight Used", v: `${avgWeight} kg` },
              { l: "Min Area / Head", v: `${mpiMinArea} m²` },
              { l: "Total Pen Area", v: `${totalArea.toFixed(1)} m²` },
              { l: "MPI Max Head", v: mpiMaxHead },
              { l: "Actual Head", v: totalHead },
              { l: "Result", v: complianceOk ? "✓ Compliant" : "✗ Exceeds Limit", color: complianceOk ? "#007a3d" : "#c00" },
            ].map((item, i) => (
              <div key={i} style={{ padding: "6px 10px", borderRight: i%2===0?"1px solid #ddd":"none", borderBottom: i<4?"1px solid #ddd":"none", background: i%2===0?"#fafafa":"#fff" }}>
                <div style={{ fontSize: 8, fontWeight: 700, textTransform: "uppercase", color: "#999", marginBottom: 2 }}>{item.l}</div>
                <div style={{ fontSize: 11, fontWeight: 700, color: item.color || "#1a1a1a" }}>{item.v}</div>
              </div>
            ))}
          </div>
          <div style={{ fontSize: 8, color: "#999", marginTop: 4, fontStyle: "italic" }}>Voyage &lt;10 days. Hospital pen area excluded.</div>
        </div>

        <div>
          <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", color: "#7a1428", marginBottom: 6, borderBottom: "1px solid #eee", paddingBottom: 4 }}>Pre-Loading Compliance Checklist</div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 9 }}>
            {[
              { k: "farmerDeclarationReceived", l: "Farmer Declaration Form received (24hrs prior)" },
              { k: "vetCertificatesUploaded", l: "Vet certificates provided" },
              { k: "loadPlanApproved", l: "Load Plan approved by Master / Stockman" },
              { k: "hospitalPenReserved", l: "Hospital pen reserved (min 1% pen space)" },
              { k: "prestartMeetingCompleted", l: "Prestart meeting completed with crew" },
            ].map((item, i) => (
              <tr key={i} style={{ borderBottom: "1px solid #eee" }}>
                <td style={{ padding: "4px 6px", width: 20, textAlign: "center", color: checklist[item.k] ? "#007a3d" : "#c00", fontWeight: 700, fontSize: 12 }}>{checklist[item.k] ? "✓" : "✗"}</td>
                <td style={{ padding: "4px 6px" }}>{item.l}</td>
              </tr>
            ))}
          </table>
        </div>
      </div>

      {/* Livestock consignment detail */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", color: "#7a1428", marginBottom: 6, borderBottom: "1px solid #eee", paddingBottom: 4 }}>Livestock Consignment Detail</div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 9 }}>
          <thead><tr>{["Booking Ref","Customer","Species","Class","Head","Avg Wt (kg)","Last Fed","Last Watered","Fit for Transport","Vet Cert"].map(h => <th key={h} style={{ ...th, background: "#444" }}>{h}</th>)}</tr></thead>
          <tbody>
            {bookings.filter(b => b.bookingType === "livestock" || b.bookingType === "mixed").map((b, bi) =>
              (b.livestockItems || []).map((item, li) => (
                <tr key={`${bi}-${li}`} style={{ borderBottom: "1px solid #eee", background: bi%2===0?"#fff":"#fafafa" }}>
                  <td style={{ padding: "4px 8px", fontWeight: 600 }}>{b.bookingRef}</td>
                  <td style={{ padding: "4px 8px" }}>{b.customerName}</td>
                  <td style={{ padding: "4px 8px", textTransform: "capitalize" }}>{item.livestockType}</td>
                  <td style={{ padding: "4px 8px", textTransform: "capitalize" }}>{item.animalClass||"—"}</td>
                  <td style={{ padding: "4px 8px", fontWeight: 700 }}>{item.headCount}</td>
                  <td style={{ padding: "4px 8px" }}>{item.averageWeightKg||"—"}</td>
                  <td style={{ padding: "4px 8px" }}>{item.lastFed ? new Date(item.lastFed).toLocaleString("en-NZ",{dateStyle:"short",timeStyle:"short"}) : "—"}</td>
                  <td style={{ padding: "4px 8px" }}>{item.lastWatered ? new Date(item.lastWatered).toLocaleString("en-NZ",{dateStyle:"short",timeStyle:"short"}) : "—"}</td>
                  <td style={{ padding: "4px 8px", textAlign: "center", color: item.fitForTransport?"#007a3d":"#c00", fontWeight: 700 }}>{item.fitForTransport?"✓":"✗"}</td>
                  <td style={{ padding: "4px 8px", textAlign: "center", color: item.vetCertificateUploaded?"#007a3d":"#c00", fontWeight: 700 }}>{item.vetCertificateUploaded?"✓":"✗"}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Signature block */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 20, marginBottom: 14, border: "1px solid #ddd", borderRadius: 3, padding: 14 }}>
        {["Master / Skipper", "Stockman", "Date & Time of Approval"].map(label => (
          <div key={label}>
            <div style={{ fontSize: 8, fontWeight: 700, textTransform: "uppercase", color: "#999", marginBottom: 8 }}>{label}</div>
            <div style={{ borderBottom: "1px solid #1a1a1a", height: 32, marginBottom: 4 }} />
            <div style={{ fontSize: 8, color: "#bbb" }}>Name & Signature</div>
          </div>
        ))}
      </div>

      {/* Declaration */}
      <div style={{ background: "#fafafa", border: "1px solid #ddd", borderRadius: 3, padding: 10, fontSize: 8, color: "#555", lineHeight: 1.6 }}>
        <strong style={{ color: "#1a1a1a" }}>Declaration:</strong> This load plan has been prepared in accordance with MNZ Rule Part 24c and McCallum Bros Livestock Handling Procedures v1.
        All animals have been verified as fit for transport. Stocking density does not exceed the maximum permissible floor area per head (Table 4, MNZ Rule Part 24c, voyages &lt;10 days).
        A hospital pen has been reserved (min 1% of total pen floor space). All Livestock Booking &amp; Declaration Forms and vet certificates have been received where required.
      </div>

      {/* Footer */}
      <div style={{ marginTop: 12, paddingTop: 8, borderTop: "1px solid #eee", display: "flex", justifyContent: "space-between", fontSize: 8, color: "#bbb" }}>
        <span>McCallum Bros Ltd · Livestock Load Plan · {sailing.sailingRef}</span>
        <span>Livestock Handling Procedures v1 · Generated {new Date().toLocaleString("en-NZ")}</span>
      </div>
    </div>
  );
}

function ManualPenAssign({ pen, bookings, existingPens, sailingId, onSaved }) {
  const [selectedKey, setSelectedKey] = useState(""); // "${bookingId}::${itemIndex}"
  const [headCount, setHeadCount] = useState("");
  const [saving, setSaving] = useState(false);

  // Every livestock item across every booking, flattened into individually
  // selectable options — a booking can now hold multiple classes (Bull,
  // Heifer, Steer, Cow etc. all in one booking), so the old "one option per
  // booking" approach only ever showed the first class and silently hid
  // the rest.
  const allItems = bookings.flatMap(b =>
    (b.livestockItems || []).map((item, itemIndex) => ({ booking: b, item, itemIndex }))
  );

  const [selectedBookingId, selectedItemIndexStr] = selectedKey.split("::");
  const selectedEntry = allItems.find(
    e => String(e.booking._id) === selectedBookingId && String(e.itemIndex) === selectedItemIndexStr
  );
  const selectedBooking = selectedEntry?.booking;
  const livestock = selectedEntry?.item;

  const penAlloc = pen.allocations?.reduce((s, a) => s + a.headCount, 0) || 0;
  // True remaining space depends on the weight of the class being assigned,
  // not a fixed head-count — computed from the pen's real area minus what's
  // already sitting in it (by each existing allocation's own weight), only
  // falling back to the static maxHead for a pen with no area on record.
  const penAreaUsed = (pen.allocations || []).reduce(
    (s, a) => s + Number(a.headCount || 0) * getMpiMinArea(Number(a.avgWeightKg || 0)),
    0
  );
  const penArea = Number(pen.areaM2 || 0);
  const assigningMpiArea = getMpiMinArea(Number(livestock?.averageWeightKg || 0));
  const space = penArea
    ? Math.max(Math.floor((penArea - penAreaUsed) / assigningMpiArea), 0)
    : Math.max((pen.maxHead || 0) - penAlloc, 0);

  // How much of THIS SPECIFIC class (not the whole booking) is already
  // sitting in other pens — matched by bookingId AND animalClass, since a
  // booking can now have several classes each with their own separate
  // allocation progress.
  const alreadyAllocated = selectedEntry
    ? (existingPens || []).reduce((sum, p) => {
        const forThisItem = (p.allocations || []).filter(
          a => String(a.bookingId) === String(selectedBooking._id) && a.animalClass === (livestock.animalClass || livestock.livestockType)
        );
        return sum + forThisItem.reduce((s, a) => s + Number(a.headCount || 0), 0);
      }, 0)
    : 0;
  const remaining = Math.max((livestock?.headCount || 0) - alreadyAllocated, 0);
  const maxAssignable = Math.min(space, remaining);

  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">Assign Livestock to This Pen</div>
      <div className="space-y-3">
        <div>
          <label className="block text-xs text-slate-500 mb-1">Select Customer / Class</label>
          <select
            className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-slate-950"
            value={selectedKey}
            onChange={e => { setSelectedKey(e.target.value); setHeadCount(""); }}
          >
            <option value="">— Select a customer/class —</option>
            {allItems.map(({ booking: b, item, itemIndex }) => {
              const bookingTotal = item.headCount || 0;
              const itemAllocated = (existingPens || []).reduce((sum, p) => {
                const forThisItem = (p.allocations || []).filter(
                  a => String(a.bookingId) === String(b._id) && a.animalClass === (item.animalClass || item.livestockType)
                );
                return sum + forThisItem.reduce((s, a) => s + Number(a.headCount || 0), 0);
              }, 0);
              const itemRemaining = Math.max(bookingTotal - itemAllocated, 0);
              const key = `${b._id}::${itemIndex}`;
              return (
                <option key={key} value={key} disabled={itemRemaining === 0}>
                  {b.bookingRef || b._id?.slice(-6).toUpperCase()} — {b.customerName} · {item.animalClass || item.livestockType || "—"} · {itemRemaining} of {bookingTotal} head remaining{itemRemaining === 0 ? " — fully allocated" : ""}
                </option>
              );
            })}
          </select>
        </div>

        {selectedEntry && (
          <>
            <div className="rounded-xl bg-slate-50 border border-slate-100 px-3 py-2 text-xs text-slate-600 space-y-1">
              <div><span className="font-semibold">Class:</span> {livestock?.animalClass || "—"} · <span className="font-semibold">Avg weight:</span> {livestock?.averageWeightKg || "—"} kg</div>
              <div><span className="font-semibold">Total head in this class:</span> {livestock?.headCount || 0}</div>
              {alreadyAllocated > 0 && (
                <div><span className="font-semibold">Already allocated (other pens):</span> {alreadyAllocated}</div>
              )}
              <div className={remaining === 0 ? "font-semibold text-red-600" : ""}>
                <span className="font-semibold">Remaining unallocated:</span> {remaining}
              </div>
              <div><span className="font-semibold">Space in this pen:</span> {space} head</div>
            </div>
            {remaining === 0 ? (
              <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">
                This class's full headcount is already allocated to other pens. Move animals from an existing pen instead of assigning more.
              </div>
            ) : (
              <div>
                <label className="block text-xs text-slate-500 mb-1">Head Count to Assign</label>
                <input
                  type="number"
                  min="1"
                  max={maxAssignable}
                  value={headCount}
                  onChange={e => setHeadCount(e.target.value)}
                  placeholder={`Max ${maxAssignable}`}
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-slate-950"
                />
              </div>
            )}
            <button
              disabled={!headCount || Number(headCount) <= 0 || Number(headCount) > maxAssignable || saving}
              onClick={async () => {
                if (!headCount || Number(headCount) <= 0 || Number(headCount) > maxAssignable) return;
                setSaving(true);
                try {
                  const newAlloc = {
                    bookingId: selectedBooking._id,
                    customerName: selectedBooking.customerName || "—",
                    animalClass: livestock?.animalClass || livestock?.livestockType || "cattle",
                    avgWeightKg: Number(livestock?.averageWeightKg || 450),
                    headCount: Number(headCount),
                    mpiMinAreaPerHead: getMpiMinArea(Number(livestock?.averageWeightKg || 450)),
                  };
                  // Build updated pens — ensure pen exists in list
                  let pens = existingPens.length ? [...existingPens] : [];
                  const penIdx = pens.findIndex(p => p.penNumber === pen.penNumber || p.penLabel === pen.penLabel);
                  if (penIdx >= 0) {
                    pens[penIdx] = { ...pens[penIdx], allocations: [...(pens[penIdx].allocations || []), newAlloc] };
                  } else {
                    pens.push({ ...pen, allocations: [newAlloc] });
                  }
                  await fetch(`${API_BASE}/sailings/${sailingId}/loadplan`, {
                    method: "PATCH",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ pens, status: "draft" }),
                  });
                  await onSaved();
                } catch (err) { alert("Failed to assign: " + err.message); }
                finally { setSaving(false); }
              }}
              className="w-full rounded-xl bg-slate-950 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-40"
            >
              {saving ? "Assigning..." : "Assign to Pen"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function LoadingVesselDiagram({ pens, vessel, onPenClick }) {
  const penNumSort = (a, b) => {
    // The Container slot always sorts to the end of its row — its
    // penNumber field isn't reliably a parseable "P11"-style value, so
    // treat it as effectively infinite rather than let a failed parse
    // silently fall back to 0 (sorting it first, which was the bug).
    if (a.isContainerSlot) return 1;
    if (b.isContainerSlot) return -1;
    const na = parseInt(String(a.penNumber || "").replace(/\D/g, ""), 10) || 0;
    const nb = parseInt(String(b.penNumber || "").replace(/\D/g, ""), 10) || 0;
    return na - nb;
  };
  const portPens = pens.filter(p => {
    const z = vessel?.zones?.find(z => z.penNumber === p.penNumber || z.name === p.penLabel);
    return z?.row === "port" || p.penNumber?.startsWith("P");
  }).sort(penNumSort);
  const starPens = pens.filter(p => {
    const z = vessel?.zones?.find(z => z.penNumber === p.penNumber || z.name === p.penLabel);
    return z?.row === "starboard" || p.penNumber?.startsWith("S");
  }).sort(penNumSort);

  const PH = 66, GAP = 7, WALK = 38, LPAD = 42, TPAD = 10;
  // Pen width is proportional to its real areaM2, not a fixed uniform
  // size — both rows are scaled to the SAME target width, since Port and
  // Starboard both span the same real vessel length (43.5m), just
  // subdivided differently. A minimum width keeps small pens (e.g. the
  // Hospital pen) legible.
  const ROW_PX = 900;
  const MIN_PW = 30;
  // The Loading Ramp sits explicitly between the 5th and 6th Port pens
  // (P5/P6) — confirmed directly, not computed from a proportion. It's
  // real floor space, so it gets its own pen-sized cell in the row rather
  // than a thin divider line.
  const RAMP_AFTER_INDEX = 5; // insert after this many pens (0-indexed count)
  const RAMP_GAP_PX = 48;

  const layoutRow = (penList, insertRampGap) => {
    const totalArea = penList.reduce((s, p) => s + (Number(p.areaM2) || 1), 0) || 1;
    // Reserve extra space for the ramp box PLUS a matching gap on each
    // side of it (previously only the "before" side got a gap — the pen
    // right after the ramp butted straight up against it with zero space).
    const rampReserve = insertRampGap ? RAMP_GAP_PX + GAP : 0;
    const usablePx = insertRampGap ? ROW_PX - rampReserve : ROW_PX;

    let acc = 0;
    const raw = penList.map(p => {
      const w = Math.max(MIN_PW, (Number(p.areaM2) || 1) / totalArea * usablePx);
      const startAcc = acc;
      acc += w + GAP;
      return { pen: p, w, startAcc };
    });
    const totalRawWidth = acc;

    let gapX = null;
    const items = raw.map((item, i) => {
      const pastGap = insertRampGap && i >= RAMP_AFTER_INDEX;
      if (pastGap && gapX === null) gapX = LPAD + item.startAcc;
      return { pen: item.pen, x: LPAD + item.startAcc + (pastGap ? rampReserve : 0), w: item.w };
    });
    if (insertRampGap && gapX === null) gapX = LPAD + totalRawWidth;

    return { items, gapX };
  };
  const portResult = layoutRow(portPens, true);
  const starResult = layoutRow(starPens, false);
  const portLayout = portResult.items;
  const starLayout = starResult.items;
  const rowEnd = (layout) => layout.length ? layout[layout.length - 1].x + layout[layout.length - 1].w : LPAD;
  const cols = Math.max(rowEnd(portLayout), rowEnd(starLayout));
  const W = cols + 10;
  const H = TPAD + PH + WALK + PH + TPAD + 26;

  // Universal, weight-aware capacity fraction for a pen: real area used
  // (each allocation's own head count × its own MPI density) divided by
  // the pen's real area. Works correctly whether the pen holds one class
  // or several different classes/weights at once, unlike a plain
  // headCount/maxHead ratio, which only happens to be right when the
  // pen's static maxHeadCount matches the true MPI max for that weight.
  const penCapacityPct = (pen) => {
    const area = Number(pen.areaM2 || 0);
    const alloc = pen.allocations?.reduce((s, a) => s + a.headCount, 0) || 0;
    if (!area) return pen.maxHead > 0 ? alloc / pen.maxHead : 0;
    const weights = [...new Set((pen.allocations || []).map(a => Number(a.avgWeightKg || 0)).filter(Boolean))];
    if (weights.length === 1) {
      // Single class: compare head count to head count. MPI max head is
      // always floored (no fractional animals), so a genuinely full pen
      // always leaves a sliver of physical area unused — comparing area
      // instead of head count would show a "full" pen as 97% rather than
      // 100%, which reads as wrong even though it's technically accurate.
      const mpiMax = Math.floor(area / getMpiMinArea(weights[0]));
      return mpiMax > 0 ? alloc / mpiMax : 0;
    }
    // Mixed classes have no single head-count ceiling to compare against,
    // so this is the one case where the raw area ratio is the right and
    // only meaningful number.
    const areaUsed = (pen.allocations || []).reduce(
      (s, a) => s + Number(a.headCount || 0) * getMpiMinArea(Number(a.avgWeightKg || 0)),
      0
    );
    return area ? areaUsed / area : 0;
  };
  // A single "max head" number is only meaningful when the pen holds one
  // weight class — for a mixed pen (like the S3-style shared pens) there
  // isn't one true head-count ceiling, so this returns null and the tile
  // shows the allocated count alone rather than a misleading fraction.
  const penDisplayMax = (pen) => {
    const area = Number(pen.areaM2 || 0);
    if (!area) return pen.maxHead;
    const weights = [...new Set((pen.allocations || []).map(a => Number(a.avgWeightKg || 0)).filter(Boolean))];
    if (weights.length !== 1) return null;
    return Math.floor(area / getMpiMinArea(weights[0]));
  };

  const penFill = (pen) => {
    if (pen.isHospitalPen) return "#fee2e2";
    const alloc = pen.allocations?.reduce((s, a) => s + a.headCount, 0) || 0;
    if (!alloc) return "#f8fafc";
    const pct = penCapacityPct(pen);
    return pct >= 0.95 ? "#fef2f2" : pct >= 0.8 ? "#fffbeb" : "#f0fdf4";
  };
  const penStroke = (pen) => {
    if (pen.isHospitalPen) return "#fca5a5";
    const alloc = pen.allocations?.reduce((s, a) => s + a.headCount, 0) || 0;
    if (!alloc) return "#e2e8f0";
    const pct = penCapacityPct(pen);
    return pct >= 0.95 ? "#f87171" : pct >= 0.8 ? "#fbbf24" : "#86efac";
  };

  const renderRow = (layout, yOff) => layout.map(({ pen, x, w }, i) => {
    const alloc = pen.allocations?.reduce((s, a) => s + a.headCount, 0) || 0;
    const pct = alloc ? Math.round(penCapacityPct(pen) * 100) : 0;
    const displayMax = penDisplayMax(pen);
    const pctCol = pct >= 95 ? "#dc2626" : pct >= 80 ? "#d97706" : "#16a34a";

    if (pen.isContainerSlot) {
      // Container slots are a different capacity type entirely (TEU, not
      // livestock head) — distinct blue styling so they're unmistakably
      // not a livestock pen. Real assignment/allocation of freight
      // bookings into these slots isn't built yet — this shows the
      // reserved capacity only.
      return (
        <g key={i} style={{ cursor: "default" }}>
          <rect x={x} y={yOff} width={w} height={PH} fill="#eff6ff" stroke="#93c5fd" strokeWidth={1.5} rx={5} />
          <text x={x + w/2} y={yOff + 15} textAnchor="middle" fontSize={9.5} fontWeight="700" fill="#475569">{pen.penNumber}</text>
          <text x={x + w/2} y={yOff + 33} textAnchor="middle" fontSize={8} fontWeight="700" fill="#1d4ed8">Container</text>
          <text x={x + w/2} y={yOff + 46} textAnchor="middle" fontSize={8.5} fontWeight="700" fill="#1e3a8a">{pen.maxTEU || 0} TEU</text>
          <text x={x + w/2} y={yOff + 58} textAnchor="middle" fontSize={7} fill="#93c5fd">Slot</text>
        </g>
      );
    }

    return (
      <g key={i} style={{ cursor: "pointer" }} onClick={() => onPenClick(pen)}>
        <rect x={x} y={yOff} width={w} height={PH} fill={penFill(pen)} stroke={penStroke(pen)} strokeWidth={1.5} rx={5} />
        <text x={x + w/2} y={yOff + 15} textAnchor="middle" fontSize={9.5} fontWeight="700" fill="#475569">{pen.penNumber}</text>
        {pen.isHospitalPen
          ? <text x={x + w/2} y={yOff + 36} textAnchor="middle" fontSize={8.5} fill="#dc2626" fontWeight="700">HOSP</text>
          : alloc > 0 ? <>
            <text x={x + w/2} y={yOff + 33} textAnchor="middle" fontSize={9.5} fontWeight="700" fill="#1e293b">{displayMax != null ? `${alloc}/${displayMax}` : `${alloc} head`}</text>
            {(() => {
              // Wrap long customer/farmer names across two lines instead
              // of hard-cutting at 10 characters with no indication
              // anything was trimmed — e.g. "Chatham Islands Land and
              // Sea" was becoming "Chatham Is" with no ellipsis at all.
              const name = pen.allocations?.[0]?.customerName || "";
              const maxLineChars = Math.max(Math.floor(w / 6), 8);
              let lines;
              if (name.length <= maxLineChars) {
                lines = [name];
              } else {
                const words = name.split(" ");
                let line1 = "";
                let idx = 0;
                while (idx < words.length && (line1 ? line1 + " " + words[idx] : words[idx]).length <= maxLineChars) {
                  line1 = line1 ? `${line1} ${words[idx]}` : words[idx];
                  idx++;
                }
                if (!line1) { line1 = words[0]; idx = 1; }
                let line2 = words.slice(idx).join(" ");
                if (line2.length > maxLineChars) {
                  line2 = line2.slice(0, Math.max(maxLineChars - 1, 3)) + "…";
                }
                lines = line2 ? [line1, line2] : [line1];
              }
              const fontSize = lines.some(l => l.length > maxLineChars) ? 6.5 : lines.length > 1 ? 7 : 7.5;
              return lines.map((line, li) => (
                <text key={li} x={x + w/2} y={yOff + 42 + li * 7} textAnchor="middle" fontSize={fontSize} fill="#64748b">{line}</text>
              ));
            })()}
            <text x={x + w/2} y={yOff + 58} textAnchor="middle" fontSize={8.5} fontWeight="700" fill={pctCol}>{pct}%</text>
          </> : <>
            <text x={x + w/2} y={yOff + 38} textAnchor="middle" fontSize={8} fill="#94a3b8">Empty</text>
            <text x={x + w/2} y={yOff + 51} textAnchor="middle" fontSize={7.5} fill="#cbd5e1">{pen.areaM2 ? `${pen.areaM2} m²` : `${pen.maxHead} max`}</text>
          </>
        }
      </g>
    );
  });

  // Loading Ramp — the actual gap opened up in the Port row by layoutRow,
  // spanning the full pen height plus a bit into the raceway below, styled
  // as reserved floor space (hatched) rather than a thin divider line.
  const rampX = portResult.gapX;

  return (
    <div style={{ overflowX: "auto", overflowY: "hidden", width: "100%", maxHeight: 270 }}>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet" style={{ display: "block", width: "100%", height: 250, fontFamily: "Arial, sans-serif" }}>
        <rect x={LPAD - 4} y={TPAD - 4} width={W - LPAD} height={H - TPAD - 22} rx={10} fill="#f8fafc" stroke="#e2e8f0" strokeWidth={1} />
        <text x={LPAD - 10} y={TPAD + PH/2 + 4} textAnchor="middle" fontSize={9} fontWeight="700" fill="#7a1428" letterSpacing="0.5" transform={`rotate(-90,${LPAD-10},${TPAD+PH/2+4})`}>PORT</text>
        <text x={LPAD - 10} y={TPAD + PH + WALK + PH/2 + 4} textAnchor="middle" fontSize={9} fontWeight="700" fill="#1e3a5c" letterSpacing="0.5" transform={`rotate(-90,${LPAD-10},${TPAD+PH+WALK+PH/2+4})`}>STBD</text>
        <rect x={LPAD} y={TPAD + PH + 2} width={cols - LPAD} height={WALK - 4} fill="#eef2f7" stroke="#cbd5e1" strokeWidth={0.5} strokeDasharray="4,3" rx={4} />
        <text x={LPAD + (cols - LPAD)/2} y={TPAD + PH + WALK/2 + 4} textAnchor="middle" fontSize={8.5} fontWeight="700" fill="#334155">Raceway — click any pen to assign</text>
        {/* Loading Ramp — its own pen-sized cell in the Port row, matching P5/P6 */}
        <g>
          <rect x={rampX} y={TPAD} width={RAMP_GAP_PX} height={PH} fill="#cbd5e1" stroke="#94a3b8" strokeWidth={1.5} rx={4} />
          <text x={rampX + RAMP_GAP_PX / 2} y={TPAD + PH / 2 - 10} textAnchor="middle" fontSize={11} fill="#475569">↑</text>
          <text x={rampX + RAMP_GAP_PX / 2} y={TPAD + PH / 2 + 4} textAnchor="middle" fontSize={7.5} fontWeight="700" fill="#334155">RAMP</text>
          <text x={rampX + RAMP_GAP_PX / 2} y={TPAD + PH / 2 + 20} textAnchor="middle" fontSize={11} fill="#475569">↓</text>
        </g>
        {renderRow(portLayout, TPAD)}
        {renderRow(starLayout, TPAD + PH + WALK)}
        {[["#f0fdf4","#86efac","Allocated"],["#fffbeb","#fbbf24","80–94%"],["#fef2f2","#f87171","≥95%"],["#fee2e2","#fca5a5","Hospital"],["#f8fafc","#e2e8f0","Empty"]].map(([fill,stroke,label],i) => (
          <g key={i} transform={`translate(${LPAD + i * 98}, ${H - 16})`}>
            <rect width={10} height={10} fill={fill} stroke={stroke} strokeWidth={1} rx={2}/>
            <text x={14} y={9} fontSize={7.5} fill="#64748b">{label}</text>
          </g>
        ))}
      </svg>
    </div>
  );
}

function FreightDeckDiagram({ bookings }) {
  const items = bookings.flatMap(b =>
    (b.freightItems || []).map(item => ({
      ref: b.bookingRef || b._id?.slice(-6).toUpperCase(),
      customer: b.customerName,
      type: item.unitType || item.freightType || b.bookingType,
      desc: item.description || b.machinery?.description || "—",
      qty: item.quantity || 1,
      weight: item.weightKg || b.machinery?.weightKg || 0,
      bookingId: b._id,
    }))
  );

  if (!items.length) return null;

  const SLOT_W = 90, SLOT_H = 60, GAP = 4, COLS = 8, LPAD = 8, TPAD = 8;
  const rows = Math.ceil(items.length / COLS);
  const W = LPAD + COLS * (SLOT_W + GAP) + LPAD;
  const H = TPAD + rows * (SLOT_H + GAP) + 24;

  const typeColor = (type) => {
    const t = String(type || "").toLowerCase();
    if (t.includes("vehicle") || t.includes("machinery") || t.includes("roro")) return { fill: "#eff6ff", stroke: "#93c5fd" };
    if (t.includes("container")) return { fill: "#fdf4ff", stroke: "#d8b4fe" };
    if (t.includes("hazard") || t.includes("dg")) return { fill: "#fff7ed", stroke: "#fb923c" };
    return { fill: "#f8fafc", stroke: "#cbd5e1" };
  };

  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Deck Layout</div>
      <div style={{ overflowX: "auto" }}>
        <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: "block", fontFamily: "Arial, sans-serif" }}>
          <rect x={0} y={0} width={W} height={H - 18} rx={8} fill="#f1f5f9" stroke="#e2e8f0" strokeWidth={1} />
          {items.map((item, i) => {
            const col = i % COLS;
            const row = Math.floor(i / COLS);
            const x = LPAD + col * (SLOT_W + GAP);
            const y = TPAD + row * (SLOT_H + GAP);
            const { fill, stroke } = typeColor(item.type);
            return (
              <g key={i}>
                <rect x={x} y={y} width={SLOT_W} height={SLOT_H} fill={fill} stroke={stroke} strokeWidth={1.5} rx={3} />
                <text x={x + SLOT_W/2} y={y + 14} textAnchor="middle" fontSize={7} fontWeight="700" fill="#475569">{item.ref}</text>
                <text x={x + SLOT_W/2} y={y + 24} textAnchor="middle" fontSize={6.5} fill="#64748b">{(item.customer || "").substring(0, 12)}</text>
                <text x={x + SLOT_W/2} y={y + 34} textAnchor="middle" fontSize={6.5} fill="#94a3b8" textLength={Math.min(SLOT_W - 8, item.desc.length * 5)}>{item.desc.substring(0, 14)}</text>
                <text x={x + SLOT_W/2} y={y + 44} textAnchor="middle" fontSize={6.5} fill="#94a3b8" textTransform="capitalize">{item.type} · {item.qty}</text>
                {item.weight > 0 && <text x={x + SLOT_W/2} y={y + 54} textAnchor="middle" fontSize={6} fill="#94a3b8">{item.weight.toLocaleString()}kg</text>}
              </g>
            );
          })}
          {[["#eff6ff","#93c5fd","Vehicle/RoRo"],["#fdf4ff","#d8b4fe","Container"],["#fff7ed","#fb923c","DG/Hazardous"],["#f8fafc","#cbd5e1","General Freight"]].map(([fill,stroke,label],i) => (
            <g key={i} transform={`translate(${LPAD + i * 110}, ${H - 14})`}>
              <rect width={9} height={9} fill={fill} stroke={stroke} strokeWidth={1} rx={1.5}/>
              <text x={12} y={8} fontSize={7} fill="#64748b">{label}</text>
            </g>
          ))}
        </svg>
      </div>
    </div>
  );
}

function AllocationSummary({ pens }) {
  const [open, setOpen] = useState(false);
  const rows = pens.flatMap(pen => {
    if (!pen.allocations?.length) {
      return pen.isHospitalPen ? [{ pen, alloc: null }] : [];
    }
    // Merge allocations that share the same customer + class within this
    // pen — two separate bookings both landing in the same pen as the same
    // customer/class read as one combined consignment here, not two odd
    // half-rows.
    const grouped = [];
    pen.allocations.forEach(alloc => {
      const existing = grouped.find(g => g.customerName === alloc.customerName && g.animalClass === alloc.animalClass);
      if (existing) {
        existing.headCount += alloc.headCount;
      } else {
        grouped.push({ ...alloc });
      }
    });
    return grouped.map(alloc => ({ pen, alloc }));
  });
  const allocatedPens = pens.filter(p => p.allocations?.length > 0).length;

  return (
    <div className="mt-6 rounded-2xl border border-slate-200 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-3.5 bg-slate-50 border-b border-slate-200 hover:bg-slate-100 transition"
      >
        <div className="flex items-center gap-3">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Allocation Summary</span>
          <span className="text-xs font-semibold text-slate-400">{allocatedPens} of {pens.length} pens allocated</span>
        </div>
        <span className="text-slate-400 text-sm">{open ? "▲ Hide" : "▼ Show"}</span>
      </button>

      {open && (
        <>
          <div className="grid grid-cols-[70px_1fr_100px_70px_90px_90px] gap-2 px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-slate-400 border-b border-slate-100 bg-white">
            <div>Pen</div>
            <div>Customer</div>
            <div>Class</div>
            <div>Head</div>
            <div>Avg Weight</div>
            <div>Capacity %</div>
          </div>
          {rows.map(({ pen, alloc }, i) => {
            // Same logic as the printed Load Plan (LoadPlanPrint.jsx): a
            // pen shared by more than one allocation shows its true
            // combined area usage, not each row's headCount against the
            // full pen independently; a single-class pen uses that
            // class's own MPI max, not the pen's static maxHeadCount.
            const area = Number(pen.areaM2 || 0);
            const isSharedPen = (pen.allocations?.length || 0) > 1;
            let pct = 0;
            if (alloc) {
              if (area) {
                if (isSharedPen) {
                  const totalPenAreaUsed = pen.allocations.reduce(
                    (s, a) => s + Number(a.headCount || 0) * getMpiMinArea(Number(a.avgWeightKg || 0)),
                    0
                  );
                  pct = Math.round((totalPenAreaUsed / area) * 100);
                } else {
                  const mpiMax = Math.floor(area / getMpiMinArea(Number(alloc.avgWeightKg || 0)));
                  pct = mpiMax > 0 ? Math.round((alloc.headCount / mpiMax) * 100) : 0;
                }
              } else {
                pct = pen.maxHead > 0 ? Math.round((alloc.headCount / pen.maxHead) * 100) : 0;
              }
            }
            const pctColor = pct >= 95 ? "text-red-600" : pct >= 80 ? "text-amber-600" : "text-emerald-600";
            return (
              <div
                key={i}
                className={`grid grid-cols-[70px_1fr_100px_70px_90px_90px] gap-2 px-4 py-2.5 text-sm items-center ${i > 0 ? "border-t border-slate-100" : ""} ${pen.isHospitalPen ? "bg-red-50" : ""}`}
              >
                <div className="font-semibold text-slate-900 text-xs">{pen.penNumber || pen.penLabel}</div>
                <div className="text-slate-700 truncate text-xs">{alloc ? alloc.customerName : pen.isHospitalPen ? "Hospital — Reserved" : <span className="text-slate-300">Empty</span>}</div>
                <div className="text-slate-500 text-xs capitalize">{alloc?.animalClass || "—"}</div>
                <div className="font-semibold text-slate-900 text-xs">{alloc ? alloc.headCount : ""}</div>
                <div className="text-slate-500 text-xs">{alloc?.avgWeightKg ? `${alloc.avgWeightKg} kg` : "—"}</div>
                <div className={`text-xs font-bold ${alloc ? pctColor : "text-slate-300"}`}>{alloc ? `${pct}%` : "—"}</div>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}

function PenCard({ pen, onClick }) {
  const allocated = pen.allocations?.reduce((s, a) => s + Number(a.headCount || 0), 0) || 0;
  // Same area-first, weight-aware capacity as the vessel diagram tiles —
  // a static maxHead can't tell 380kg bulls from 234kg steers, so it's
  // only trusted as a fallback when the pen has no recorded area.
  const area = Number(pen.areaM2 || 0);
  const weights = [...new Set((pen.allocations || []).map(a => Number(a.avgWeightKg || 0)).filter(Boolean))];
  const displayMax = area ? (weights.length === 1 ? Math.floor(area / getMpiMinArea(weights[0])) : null) : pen.maxHead;
  // Single class: head count against head count, so a genuinely full pen
  // reads as exactly 100% (MPI max head is always floored, so comparing
  // raw area instead would leave a full pen showing ~97%, not 100%).
  // Mixed classes have no single head-count ceiling, so area is the only
  // meaningful comparison there.
  let pct;
  if (area && displayMax != null) {
    pct = displayMax > 0 ? Math.round((allocated / displayMax) * 100) : 0;
  } else if (area) {
    const areaUsed = (pen.allocations || []).reduce(
      (s, a) => s + Number(a.headCount || 0) * getMpiMinArea(Number(a.avgWeightKg || 0)),
      0
    );
    pct = Math.round((areaUsed / area) * 100);
  } else {
    pct = pen.maxHead > 0 ? Math.round((allocated / pen.maxHead) * 100) : 0;
  }
  const isHospital = pen.isHospitalPen;
  const isEmpty = allocated === 0 && !isHospital;

  return (
    <button
      type="button"
      onClick={() => onClick && onClick(pen)}
      className={`rounded-xl border p-2.5 text-center w-full transition hover:shadow-md hover:scale-105 ${
        isHospital ? "border-red-200 bg-red-50" :
        isEmpty ? "border-slate-200 bg-slate-50 hover:border-slate-300" :
        pct >= 90 ? "border-amber-200 bg-amber-50" :
        "border-emerald-200 bg-emerald-50"
      }`}
    >
      <div className={`text-[10px] font-bold uppercase tracking-wider mb-1 ${isHospital ? "text-red-500" : "text-slate-400"}`}>
        {pen.penNumber || pen.penLabel}
      </div>
      {isHospital ? (
        <div className="text-[10px] text-red-500 font-semibold">Hospital</div>
      ) : (
        <>
          <div className={`text-sm font-bold ${isEmpty ? "text-slate-400" : "text-slate-900"}`}>
            {displayMax != null ? `${allocated}/${displayMax}` : `${allocated} head`}
          </div>
          <div className="mt-1 h-1 rounded-full bg-slate-200 overflow-hidden">
            <div
              className={`h-1 rounded-full ${pct >= 90 ? "bg-amber-400" : "bg-emerald-500"}`}
              style={{ width: `${pct}%` }}
            />
          </div>
          {pen.allocations?.[0] && (
            <div className="text-[9px] text-slate-500 mt-1 truncate">{pen.allocations[0].customerName}</div>
          )}
        </>
      )}
    </button>
  );
}

function getPercent(value, total) {
  if (!total) return 0;
  return Math.min((Number(value || 0) / Number(total || 0)) * 100, 100);
}

function toTitle(value) {
  return String(value || "")
    .replace(/_/g, " ")
    .replace(/-/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("en-NZ", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function toLocalInputValue(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Shows a date/time; click it to edit inline, then Save / Cancel.
function EditableDateRow({ label, value, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const startEdit = () => {
    setDraft(toLocalInputValue(value));
    setError("");
    setEditing(true);
  };

  const save = async () => {
    try {
      setSaving(true);
      setError("");
      await onSave(draft);
      setEditing(false);
    } catch (err) {
      setError(err.message || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-start justify-between gap-2">
      <span className="text-xs text-slate-400 shrink-0 pt-1">{label}</span>
      {editing ? (
        <div className="flex flex-col items-end gap-1.5">
          <DateTimePicker
            value={draft}
            onChange={(val) => setDraft(val)}
            placeholder="Select date & time"
          />
          <div className="flex gap-1.5">
            <button type="button" onClick={() => setEditing(false)} disabled={saving}
              className="rounded-lg border border-slate-200 px-2.5 py-1 text-[11px] font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50">
              Cancel
            </button>
            <button type="button" onClick={save} disabled={saving}
              className="rounded-lg bg-slate-900 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-slate-800 disabled:opacity-50">
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
          {error && <span className="text-[11px] text-red-600 text-right max-w-[220px]">{error}</span>}
        </div>
      ) : (
        <button type="button" onClick={startEdit} title="Click to edit"
          className="text-xs font-medium text-slate-700 text-right rounded-md px-1.5 py-1 -my-1 hover:bg-slate-100 hover:text-slate-900 transition">
          {value ? formatDate(value) : "Set date"}
        </button>
      )}
    </div>
  );
}